import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import NavigationIcon from "@mui/icons-material/Navigation";
import StarIcon from "@mui/icons-material/Star";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import apiService, {
  parseSearchPaymentDetailsResponse,
} from "../../services/api";
import { DATE_FORMATS } from "../../constants";
import useHubs from "../../hooks/useHubs";
import useRBAC, { ROLES } from "../../hooks/useRBAC";
import { useAppSelector } from "../../hooks/useAuth";
import type { RootState } from "../../store";
import { getRoleHubRelations } from "../../utils/cookieUtils";
import {
  pickVehicleCardImageFromApiField,
  readVehicleImagesJsonFromRow,
} from "../../utils/mediaUtils";
import useCodeCommonByCategory from "../../hooks/useCodeCommonByCategory";
import CustomLoading from "../../components/common/CustomLoading";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import { useNotification } from "../../contexts/NotificationContext";
import SelectPlanStep from "./edit-booking-steps/SelectPlanStep";
import {
  SelectVehicleModelStep,
  SelectVehicleStep,
  type VehicleModelOption,
  type YourSelectionVehicleDetailStrings,
  type VehicleOption,
  type PlanOption,
  type CostBreakdownItem,
} from "./edit-booking-steps";
import {
  THEME_PRIMARY,
  THEME_PRIMARY_LIGHT,
  THEME_PRIMARY_TINT,
} from "./bookingTheme";
import dayjs from "dayjs";
import {
  isIndianWallClockAfterTwoPm,
  toBookingPlanEndUtcIso,
  toBookingPlanStartUtcIso,
} from "../../utils/formatters";

const BASE_IMAGE_URL = `${process.env.REACT_APP_BASE_IMAGE_URL || ""}`.replace(
  /\/?$/,
  "/",
);

/** Razorpay order create response → order id string. */
function extractRazorpayOrderId(orderRes: unknown): string | undefined {
  if (orderRes == null || typeof orderRes !== "object") return undefined;
  const o = orderRes as Record<string, unknown>;
  const nested = o.data;
  if (nested != null && typeof nested === "object") {
    const d = nested as Record<string, unknown>;
    const id = d.id ?? d.orderId ?? d.order_id;
    if (typeof id === "string" && id.trim() !== "") return id.trim();
  }
  const top = o.id ?? o.orderId ?? o.order_id;
  if (typeof top === "string" && top.trim() !== "") return top.trim();
  return undefined;
}

function extractPaymentLinkFromCollectionResponse(res: unknown): string {
  let node: unknown = res;
  if (node != null && typeof node === "object" && "data" in (node as object)) {
    const inner = (node as { data: unknown }).data;
    if (inner != null && typeof inner === "object") node = inner;
  }
  if (node == null || typeof node !== "object") return "";
  const o = node as Record<string, unknown>;
  const readLink = (obj: Record<string, unknown>): string => {
    const link =
      obj.paymentLink ??
      obj.payment_link ??
      obj.short_url ??
      obj.shortUrl ??
      obj.url;
    return typeof link === "string" && link.trim() !== "" ? link.trim() : "";
  };
  const direct = readLink(o);
  if (direct) return direct;
  const attrs = o.Attributes ?? o.attributes;
  if (attrs != null && typeof attrs === "object") {
    return readLink(attrs as Record<string, unknown>);
  }
  return "";
}

function extractQrImageSrcFromCollectionResponse(res: unknown): string {
  let node: unknown = res;
  if (node != null && typeof node === "object" && "data" in (node as object)) {
    const inner = (node as { data: unknown }).data;
    if (inner != null && typeof inner === "object") node = inner;
  }
  if (node == null || typeof node !== "object") return "";
  const o = node as Record<string, unknown>;
  const readImg = (obj: Record<string, unknown>): string => {
    const img =
      obj.image ??
      obj.qrImage ??
      obj.qr_image ??
      obj.imageUrl ??
      obj.image_url ??
      obj.qr_code_image ??
      obj.qrcode;
    if (typeof img !== "string" || img.trim() === "") return "";
    const s = img.trim();
    if (
      s.startsWith("data:") ||
      s.startsWith("http://") ||
      s.startsWith("https://")
    )
      return s;
    return `data:image/png;base64,${s}`;
  };
  const direct = readImg(o);
  if (direct) return direct;
  const attrs = o.Attributes ?? o.attributes;
  if (attrs != null && typeof attrs === "object") {
    return readImg(attrs as Record<string, unknown>);
  }
  return "";
}

function extractPaymentIdFromCollectionResponse(
  res: unknown,
  fallback: string,
): string {
  let node: unknown = res;
  if (node != null && typeof node === "object" && "data" in (node as object)) {
    const inner = (node as { data: unknown }).data;
    if (inner != null && typeof inner === "object") node = inner;
  }
  const readId = (obj: Record<string, unknown>): string | null => {
    for (const k of [
      "paymentId",
      "paymentid",
      "PaymentId",
      "payment_id",
      "qrId",
      "qr_id",
      "qrcode_id",
    ]) {
      const v = obj[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
    const id = obj.id;
    if (id != null && String(id).trim() !== "") return String(id).trim();
    return null;
  };
  if (node != null && typeof node === "object") {
    const o = node as Record<string, unknown>;
    const direct = readId(o);
    if (direct) return direct;
    /** Create QR response: `{ data: { Attributes: { id: "qr_..." } } }` */
    const attrs = o.Attributes ?? o.attributes;
    if (attrs != null && typeof attrs === "object") {
      const nested = readId(attrs as Record<string, unknown>);
      if (nested) return nested;
    }
  }
  return fallback;
}

function extractUpdateBookingSuccessMessage(res: unknown): string | null {
  if (res == null || typeof res !== "object") return null;
  const m = (res as { message?: unknown }).message;
  if (typeof m === "string" && m.trim() !== "") return m.trim();
  return null;
}

function toBookingPlanIdNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function extractBookingPlanIdFromApiResponse(res: unknown): number | null {
  const r = res as { data?: unknown; result?: unknown } | null | undefined;
  const pick = (o: Record<string, unknown>): number | null => {
    const id = o.bookingplanid ?? o.bookingPlanId;
    if (id == null || id === "") return null;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  if (r && typeof r === "object") {
    const fromTop = pick(r as Record<string, unknown>);
    if (fromTop != null) return fromTop;
  }
  const raw =
    (r && typeof r === "object"
      ? ((r as { data?: unknown }).data ?? (r as { result?: unknown }).result)
      : undefined) ?? r;
  const single = Array.isArray(raw) ? raw[0] : raw;
  if (!single || typeof single !== "object") return null;
  return pick(single as Record<string, unknown>);
}

function resolveEditBookingPlanIdForPayment(
  updateRes: unknown,
  bookingDetails: Record<string, unknown> | null | undefined,
): number {
  const fromUpdate = extractBookingPlanIdFromApiResponse(updateRes);
  if (fromUpdate != null) return fromUpdate;
  return toBookingPlanIdNum(
    bookingDetails?.bookingplanid ?? bookingDetails?.bookingPlanId,
  );
}

type DowngradeCostBreakdownRaw = {
  planprice?: number;
  refundable_amount?: number;
  planmessage?: string;
  planrefundableamountmessage?: string;
};

function isStrictlyNegative(n: unknown): boolean {
  const v = Number(n);
  return Number.isFinite(v) && v < 0;
}

/** Only when cost API shows a downgrade (strictly negative delta). */
function shouldIssueDowngradeRefunds(raw: DowngradeCostBreakdownRaw): boolean {
  return (
    isStrictlyNegative(raw.planprice) ||
    isStrictlyNegative(raw.refundable_amount)
  );
}

function assertAddRefundResponseOk(res: unknown): void {
  if (res == null || typeof res !== "object") return;
  const o = res as {
    isSuccess?: boolean;
    success?: boolean;
    message?: string;
    Message?: string;
  };
  if (o.isSuccess === false || o.success === false) {
    const msg =
      (typeof o.message === "string" && o.message.trim() !== ""
        ? o.message.trim()
        : null) ??
      (typeof o.Message === "string" && o.Message.trim() !== ""
        ? o.Message.trim()
        : null) ??
      "Add refund failed.";
    throw new Error(msg);
  }
}

/**
 * Downgrade: one `addRefund` call with plan + deposit lines combined.
 * `amount` = refundableplanprice + refundablesecuritydeposite (each line abs when negative).
 */
async function callAddRefundForDowngradeIfNeeded(
  bookingMasterId: number,
  bookingPlanId: number,
  raw: DowngradeCostBreakdownRaw,
): Promise<void> {
  if (!shouldIssueDowngradeRefunds(raw)) return;
  if (bookingMasterId <= 0 || bookingPlanId <= 0) return;

  const planNeg = isStrictlyNegative(raw.planprice);
  const depNeg = isStrictlyNegative(raw.refundable_amount);

  const totalplanprice = Number(raw.planprice ?? 0);
  const totalrefundableamount = Number(raw.refundable_amount ?? 0);
  const refundableplanprice = planNeg
    ? Math.abs(Number.isFinite(totalplanprice) ? totalplanprice : 0)
    : 0;
  const refundablesecuritydeposite = depNeg
    ? Math.abs(
        Number.isFinite(totalrefundableamount) ? totalrefundableamount : 0,
      )
    : 0;
  const amount = refundableplanprice + refundablesecuritydeposite;
  if (amount <= 0) return;

  const planmessage = String(raw.planmessage ?? "").trim();
  const planrefundableamountmessage = String(
    raw.planrefundableamountmessage ?? "",
  ).trim();
  const descriptionParts = [
    planNeg ? planmessage || "Plan price downgrade refund" : "",
    depNeg ? planrefundableamountmessage || "Deposit downgrade refund" : "",
  ].filter((s) => s !== "");

  assertAddRefundResponseOk(
    await apiService.addRefund([
      {
        bookingmasterid: bookingMasterId,
        bookingplanid: bookingPlanId,
        amount,
        refundableplanprice,
        refundablesecuritydeposite,
        refunddescription:
          descriptionParts.length > 0
            ? descriptionParts.join(" | ")
            : "Plan downgrade refund",
        refundType: "0",
        isaddedbyweb: false,
        planmessage,
        planrefundableamountmessage,
        totalplanprice: Number.isFinite(totalplanprice) ? totalplanprice : 0,
        totalrefundableamount: Number.isFinite(totalrefundableamount)
          ? totalrefundableamount
          : 0,
      },
    ]),
  );
}

/** Inclusive calendar days between plan start and end (local start-of-day), or 0 if invalid. */
function countInclusivePlanCalendarDays(
  planStartRaw: string | null | undefined,
  planEndRaw: string | null | undefined,
): number {
  if (
    planStartRaw == null ||
    planEndRaw == null ||
    String(planStartRaw).trim() === "" ||
    String(planEndRaw).trim() === ""
  ) {
    return 0;
  }
  const s = dayjs(planStartRaw).startOf("day");
  const e = dayjs(planEndRaw).startOf("day");
  if (!s.isValid() || !e.isValid()) return 0;
  return e.diff(s, "day") + 1;
}
const steps = [
  "Select Hub",
  "Select Vehicle Model",
  "Select Vehicle",
  "Select Plan",
];

const KYC_INCOMPLETE_VEHICLE_MESSAGE =
  "Your KYC is not completed for this vehicle.";
const KYC_HIGH_SPEED_DL_MESSAGE =
  "High-speed vehicles require verified KYC and driving licence. Complete licence verification to continue.";

type CustomerKycState = {
  loading: boolean;
  checked: boolean;
  verified: boolean;
  /** From KYC `kycdetails.dl_ocr_fullname` — required with verified status for high-speed vehicles */
  hasDlOcrFullname: boolean;
};

function kycDlOcrFullnamePresent(primary: unknown): boolean {
  if (primary == null || typeof primary !== "object") return false;
  const p = primary as { kycdetails?: unknown; kycDetails?: unknown };
  const d = p.kycdetails ?? p.kycDetails;
  if (d == null || typeof d !== "object") return false;
  const rec = d as { dl_ocr_fullname?: unknown; dl_ocr_fullName?: unknown };
  const raw = rec.dl_ocr_fullname ?? rec.dl_ocr_fullName;
  return String(raw ?? "").trim() !== "";
}

function pickKycListFromResponse(res: unknown): any[] {
  const r = res as
    | { data?: unknown; result?: unknown }
    | unknown[]
    | null
    | undefined;
  if (r == null) return [];
  if (Array.isArray(r)) return r;
  const obj = r as { data?: unknown; result?: unknown };
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj.result)) return obj.result;
  return [];
}

const COUPON_APPLY_NOTIFY_MS = 3000;

function readCouponValidityMessage(body: unknown): string | null {
  if (body == null || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const m = o.message ?? o.Message;
  if (typeof m === "string" && m.trim() !== "") return m.trim();
  return null;
}

function couponValidityIndicatesFailure(body: unknown): boolean {
  if (body == null || typeof body !== "object") return false;
  const o = body as Record<string, unknown>;
  return o.isSuccess === false || o.success === false || o.IsSuccess === false;
}

function readCouponValidityData(body: unknown): Record<string, unknown> | null {
  if (body == null || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const d = o.data;
  if (d != null && typeof d === "object" && !Array.isArray(d)) {
    return d as Record<string, unknown>;
  }
  return o;
}

function readCouponDiscountValue(body: unknown): number {
  const d = readCouponValidityData(body);
  if (!d) return 0;
  const raw =
    d.discountvalue ?? d.discountValue ?? d.discount_amount ?? d.discountAmount;
  const n =
    typeof raw === "number" ? raw : Number.parseFloat(String(raw ?? ""));
  return Number.isFinite(n) ? n : 0;
}

function readCouponDisplayName(body: unknown): string {
  const d = readCouponValidityData(body);
  if (!d) return "";
  const raw =
    d.couponname ??
    d.couponName ??
    d.codename ??
    d.codeName ??
    d.qrcodelink ??
    d.qrCodeLink;
  return String(raw ?? "").trim();
}

function pickCustomerUserIdForKyc(booking: unknown): number | null {
  const tryRecord = (o: unknown): number | null => {
    if (o == null || typeof o !== "object") return null;
    const r = o as Record<string, unknown>;
    /** `createdby` / tripStart.createdby match UpdateBooking payload (renter user id from GetTripByBookingId). */
    const keys = [
      "createdbyid",
      "createdById",
      "CreatedById",
      "userid",
      "userId",
      "UserId",
      "UserID",
      "createdby",
      "CreatedBy",
      "customerid",
      "customerId",
      "CustomerId",
      "customeruserid",
      "customerUserId",
      "CustomerUserId",
      "tripuserid",
      "tripUserId",
      "TripUserId",
    ];
    for (const k of keys) {
      if (!(k in r)) continue;
      const n = Number(r[k]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  const visited = new WeakSet<object>();
  const tryDeep = (o: unknown, depth: number): number | null => {
    if (o == null || depth > 8) return null;
    if (typeof o !== "object") return null;
    if (visited.has(o as object)) return null;
    visited.add(o as object);

    const direct = tryRecord(o);
    if (direct != null) return direct;

    const b = o as Record<string, unknown>;
    const children = [
      b.tripStart,
      b.tripstart,
      b.bookingPlanModel,
      b.bookingplanmodel,
      b.user,
      b.User,
      b.booking,
      b.bookingData,
      b.bookingModel,
      b.trip,
      b.tripBooking,
    ];
    for (const c of children) {
      const found = tryDeep(c, depth + 1);
      if (found != null) return found;
    }
    return null;
  };

  return tryDeep(booking, 0);
}

/** Normalize GetTripByBookingId / wrapper envelopes to a single booking record. */
function extractBookingDetailsFromApiResponse(res: unknown): unknown | null {
  if (res == null) return null;
  const top = res as Record<string, unknown>;
  let raw: unknown = top.data ?? top.result ?? res;
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    const first = raw[0];
    return unwrapBookingRecordNode(first, 0);
  }
  return unwrapBookingRecordNode(raw, 0);
}

function unwrapBookingRecordNode(node: unknown, depth: number): unknown | null {
  if (node == null || depth > 6) return node;
  if (typeof node !== "object") return node;
  const o = node as Record<string, unknown>;
  const inner =
    o.booking ??
    o.bookingModel ??
    o.bookingData ??
    o.tripBooking ??
    o.trip ??
    o.value;
  if (inner != null && typeof inner === "object") {
    return unwrapBookingRecordNode(inner, depth + 1);
  }
  return node;
}

type StepOptionId = string | number | null;

interface EditBookingPageLocationState {
  bookingId?: string;
  bookingMasterId?: string;
  userName?: string;
  customerId?: string;
  /** Customer / renter user id from booking list row when trip payload omits userid fields */
  renterUserId?: number;
  createdby?: string;
  mobileNo?: string;
  email?: string;
  hubId?: number;
  filters?: { filters?: Record<string, string>; dateRange?: (string | null)[] };
  page?: number;
}

const EditBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const state = (location.state || {}) as EditBookingPageLocationState;
  const searchParams = new URLSearchParams(location.search);
  const bookingIdFromUrl =
    searchParams.get("bookingId") || state.bookingId || "";
  const userNameFromUrl = searchParams.get("userName") || state.userName || "";

  const stepStorageKey = bookingIdFromUrl
    ? `editBooking_step_${bookingIdFromUrl}`
    : "editBooking_step";
  const hubIdStorageKey = bookingIdFromUrl
    ? `editBooking_hubId_${bookingIdFromUrl}`
    : "editBooking_hubId";
  const modelIdStorageKey = bookingIdFromUrl
    ? `editBooking_modelId_${bookingIdFromUrl}`
    : "editBooking_modelId";
  const vehicleIdStorageKey = bookingIdFromUrl
    ? `editBooking_vehicleId_${bookingIdFromUrl}`
    : "editBooking_vehicleId";
  const planIdStorageKey = bookingIdFromUrl
    ? `editBooking_planId_${bookingIdFromUrl}`
    : "editBooking_planId";

  const [activeStep, setActiveStep] = useState(() => {
    try {
      const saved = sessionStorage.getItem(stepStorageKey);
      if (saved != null) {
        const step = Number.parseInt(saved, 10);
        if (step >= 0 && step < 4) return step;
      }
    } catch {
      // ignore
    }
    return 0;
  });
  const [selectedHubId, setSelectedHubId] = useState<number | null>(() => {
    try {
      const saved = sessionStorage.getItem(hubIdStorageKey);
      if (saved != null && saved !== "") {
        const n = Number.parseInt(saved, 10);
        if (!Number.isNaN(n)) return n;
      }
    } catch {
      // ignore
    }
    const fromState = state?.hubId;
    if (fromState != null && !Number.isNaN(Number(fromState)))
      return Number(fromState);
    return null;
  });
  const [selectedModelId, setSelectedModelId] = useState<StepOptionId>(() => {
    try {
      const saved = sessionStorage.getItem(modelIdStorageKey);
      if (saved != null && saved !== "") return saved;
    } catch {
      // ignore
    }
    return null;
  });
  const [selectedVehicleId, setSelectedVehicleId] = useState<StepOptionId>(
    () => {
      try {
        const saved = sessionStorage.getItem(vehicleIdStorageKey);
        if (saved != null && saved !== "") return saved;
      } catch {
        // ignore
      }
      return null;
    },
  );
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [bookingDetails, setBookingDetails] = useState<any>(null);
  /** Trip status from GetTripByBookingId (first element when API returns array); show checklist/upload when === 5 */
  const [tripStatusFromApi, setTripStatusFromApi] = useState<
    number | undefined
  >(undefined);
  const [vehiclePhotos, setVehiclePhotos] = useState<string[]>([]);
  const [batteryPhotos, setBatteryPhotos] = useState<string[]>([]);
  const [chargerPhotos, setChargerPhotos] = useState<string[]>([]);
  const [
    vehicleConditionChecklistMappings,
    setVehicleConditionChecklistMappings,
  ] = useState<Record<string, boolean>>({});
  const [isEditingOldChecklist, setIsEditingOldChecklist] = useState(false);
  const [newVehiclePhotos, setNewVehiclePhotos] = useState<string[]>([]);
  const [newVehicleBatteryPhotos, setNewVehicleBatteryPhotos] = useState<
    string[]
  >([]);
  const [newVehicleChargerPhotos, setNewVehicleChargerPhotos] = useState<
    string[]
  >([]);
  const [newVehicleChecklistMappings, setNewVehicleChecklistMappings] =
    useState<Record<string, boolean>>({});
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [bookingVehicleApiExtras, setBookingVehicleApiExtras] = useState<{
    imageUrl: string;
    speed: string;
    range: string;
    hubMasterId: number | null;
    modelId: number | null;
    modelName: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showVehicleUploadValidation, setShowVehicleUploadValidation] =
    useState(false);
  const [customerKycState, setCustomerKycState] = useState<CustomerKycState>({
    loading: false,
    checked: false,
    verified: true,
    hasDlOcrFullname: true,
  });

  const [couponCode, setCouponCode] = useState("");
  const [promoCodeOptions, setPromoCodeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [promoCodeMetaByValue, setPromoCodeMetaByValue] = useState<
    Record<
      string,
      { couponid: number; couponName: string; isReferralCoupon: boolean }
    >
  >({});
  const [promoCodesLoading, setPromoCodesLoading] = useState(false);
  const [couponApplyLoading, setCouponApplyLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    couponid: number;
    name: string;
    discountValue: number;
    isReferralCoupon: boolean;
  } | null>(null);
  const lastCouponValidityKeyRef = useRef("");
  const couponPlanRevalidateFlightRef = useRef(0);
  const [additionalFees, setAdditionalFees] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "online">(
    "online",
  );
  const [selectedPlanId, setSelectedPlanId] = useState<StepOptionId>(() => {
    try {
      const saved = sessionStorage.getItem(planIdStorageKey);
      if (saved != null && saved !== "") return saved;
    } catch {
      // ignore
    }
    return null;
  });
  /** Select Plan: user chose "No Plan change" — cost breakdown uses onlyvehiclechange=true with current plan ids. */
  const [onlyVehicleChangeNoPlan, setOnlyVehicleChangeNoPlan] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdownItem[]>([]);
  const [costBreakdownRaw, setCostBreakdownRaw] = useState<{
    planprice?: number;
    processing_fees?: number;
    refundable_amount?: number;
    planmessage?: string;
    planrefundableamountmessage?: string;
  }>({});
  const [costBreakdownLoading, setCostBreakdownLoading] = useState(false);
  const [totalPayable, setTotalPayable] = useState("₹0.00");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [postSavePaymentMode, setPostSavePaymentMode] = useState<
    "payment_link" | "qr_code"
  >("payment_link");
  const [onlineCollectionDialogOpen, setOnlineCollectionDialogOpen] =
    useState(false);
  const [onlineCollectionLoading, setOnlineCollectionLoading] = useState(false);
  const [onlineCollectionError, setOnlineCollectionError] = useState<
    string | null
  >(null);
  const [onlineCollectionPaymentLink, setOnlineCollectionPaymentLink] =
    useState("");
  const [onlineCollectionQrSrc, setOnlineCollectionQrSrc] = useState("");
  const [onlineCollectionOrderId, setOnlineCollectionOrderId] = useState("");
  /** From create QR / payment link response (`data.Attributes.id` or top-level id) for verify `qrId`. */
  const [onlineCollectionVerifyEntityId, setOnlineCollectionVerifyEntityId] =
    useState<string | null>(null);
  const [verifyPaymentLoading, setVerifyPaymentLoading] = useState(false);
  const [onlineCollectionUpdateDone, setOnlineCollectionUpdateDone] =
    useState(false);
  const [vehicleUnderbookingLoading, setVehicleUnderbookingLoading] =
    useState(false);
  const [cashPaymentCharge, setCashPaymentCharge] = useState<string>("");
  const [lateFeesBaseline, setLateFeesBaseline] = useState(0);
  const [lateFeesAmount, setLateFeesAmount] = useState("");

  const displayTotalPayable = useMemo(() => {
    const base =
      Number.parseFloat(String(totalPayable).replaceAll(/[₹,\s]/g, "")) || 0;
    const lateParsed =
      lateFeesAmount !== ""
        ? Number.parseFloat(String(lateFeesAmount).trim())
        : lateFeesBaseline;
    const lateCur =
      Number.isFinite(lateParsed) && lateParsed >= 0
        ? lateParsed
        : lateFeesBaseline;
    let sum = base + (lateCur - lateFeesBaseline);
    if (paymentMethod === "cash" && cashPaymentCharge !== "") {
      sum += Number(cashPaymentCharge) || 0;
    }
    const addStr = additionalFees.trim();
    if (addStr !== "") {
      const add = Number.parseFloat(addStr);
      if (Number.isFinite(add)) sum += add;
    }
    const disc = appliedCoupon?.discountValue ?? 0;
    if (Number.isFinite(disc) && disc > 0) sum -= disc;
    const safe = Number.isFinite(sum) ? Math.max(0, sum) : 0;
    return `₹${safe.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  }, [
    totalPayable,
    paymentMethod,
    cashPaymentCharge,
    additionalFees,
    lateFeesAmount,
    lateFeesBaseline,
    appliedCoupon?.discountValue,
  ]);

  /** Parsed total for save-confirm UI (match `executeSave` amount parsing). */
  const saveConfirmPayableNumeric = useMemo(
    () =>
      Number.parseFloat(
        String(displayTotalPayable).replaceAll(/[₹,\s]/g, ""),
      ) || 0,
    [displayTotalPayable],
  );

  const { hubs, loading: hubsLoading } = useHubs(setError);

  const { hasAnyRole } = useRBAC();
  const relations = getRoleHubRelations();

  const isHubOrClusterRole = hasAnyRole([
    ROLES.HUB_MANAGER,
    ROLES.HUB_CLUSTER_MANAGER,
  ]);
  const allowedHubIdsForEdit = useMemo(() => {
    if (!isHubOrClusterRole || !relations?.length) return null;
    return new Set(relations.map((r) => r.hubid));
  }, [isHubOrClusterRole, relations]);

  const displayHubs = useMemo(() => {
    if (allowedHubIdsForEdit == null) return hubs ?? [];
    return (hubs ?? []).filter((h) =>
      allowedHubIdsForEdit.has(Number(h.hubmasterid)),
    );
  }, [hubs, allowedHubIdsForEdit]);

  /** Name of the hub selected in step 0 — shown on Select Vehicle Model step */
  const selectedHubDisplayName = useMemo(() => {
    if (selectedHubId == null) return "";
    const fromDisplay = displayHubs.find(
      (h) => Number(h.hubmasterid) === Number(selectedHubId),
    );
    if (fromDisplay?.hubname) return String(fromDisplay.hubname);
    const fromAll = (hubs ?? []).find(
      (h) => Number(h.hubmasterid) === Number(selectedHubId),
    );
    if (fromAll?.hubname) return String(fromAll.hubname);
    const bHubId = bookingDetails?.hubmasterid ?? bookingDetails?.hubid;
    if (
      bookingDetails?.hubname != null &&
      Number(bHubId) === Number(selectedHubId)
    ) {
      return String(bookingDetails.hubname);
    }
    return "";
  }, [selectedHubId, displayHubs, hubs, bookingDetails]);

  const promoCustomerUserId = useMemo(() => {
    const b = bookingDetails;
    if (!b) return null;
    const fromBooking = pickCustomerUserIdForKyc(b);
    const fromNav =
      typeof state.renterUserId === "number" &&
      Number.isFinite(state.renterUserId) &&
      state.renterUserId > 0
        ? state.renterUserId
        : null;
    return fromBooking ?? fromNav;
  }, [bookingDetails, state.renterUserId]);

  const {
    statusCodes: vehicleModelCodes,
    getDisplayNameByCodeValue: getVehicleModelDisplayName,
  } = useCodeCommonByCategory("VehicleModel");
  const {
    statusCodes: evSpeedTypeCodes,
    getDisplayNameByCodeValue: getSpeedTypeDisplayName,
  } = useCodeCommonByCategory("EVSpeedType");
  const {
    statusCodes: updateVehicleRemarksCodes,
    loading: updateVehicleRemarksCodesLoading,
  } = useCodeCommonByCategory("UpdateVehicleRemarks");
  const { statusCodes: paymentmentCodes } =
    useCodeCommonByCategory("PaymentMethod");

  /** Existing booking plan was paid with cash — hide payment link / QR on save confirm (parity with plan step). */
  const originalPlanPaymentMethodWasCash = useMemo(() => {
    const b = bookingDetails as Record<string, unknown> | null | undefined;
    if (b == null) return false;
    const pmRaw = b.paymentmethod ?? b.paymentMethod;
    const pm = Number(pmRaw);
    if (!Number.isFinite(pm)) return false;
    const row = paymentmentCodes.find(
      (c) =>
        String(c.codedisplayname ?? "")
          .trim()
          .toLowerCase() === "cash",
    );
    if (!row) return false;
    const cashCode = Number.parseInt(String(row.codevalue), 10);
    if (!Number.isFinite(cashCode)) return false;
    return pm === cashCode;
  }, [bookingDetails, paymentmentCodes]);

  const updateVehicleRemarksSelectOptions = useMemo((): {
    label: string;
    value: string;
  }[] => {
    const opts: { label: string; value: string }[] = [];
    for (const code of updateVehicleRemarksCodes) {
      opts.push({
        label: String(code.codedisplayname ?? "").trim(),
        value: String(code.codevalue ?? "").trim(),
      });
    }
    return opts;
  }, [updateVehicleRemarksCodes]);

  /** Resolve "Other" from master data (e.g. codevalue 8) — do not hardcode codevalue. */
  const updateVehicleRemarksOtherCodeValue = useMemo(() => {
    const row = updateVehicleRemarksCodes.find(
      (c) =>
        String(c.codedisplayname ?? "")
          .trim()
          .toLowerCase() === "other",
    );
    return row != null ? String(row.codevalue ?? "").trim() : "";
  }, [updateVehicleRemarksCodes]);

  const isHubVehicleHighSpeed = useCallback(
    (v: any) => {
      if (v == null) return false;
      const st = v.speedtype ?? v.speedType;
      const label = String(getSpeedTypeDisplayName(st) ?? "").toLowerCase();
      return label.includes("high");
    },
    [getSpeedTypeDisplayName],
  );

  const [hubVehicles, setHubVehicles] = useState<any[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [plansData, setPlansData] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const customerName = userNameFromUrl || "—";
  const phoneNumber = state.mobileNo || "—";

  /** Renter on the booking — SavePaymentDetails must use this, not the logged-in staff user. */
  const paymentPayerName = useMemo(() => {
    const b = bookingDetails as Record<string, unknown> | null | undefined;
    const fromBooking = String(b?.name ?? b?.customerName ?? "").trim();
    if (fromBooking) return fromBooking;
    const fromUrl = String(userNameFromUrl ?? "").trim();
    if (fromUrl) return fromUrl;
    if (customerName && customerName !== "—") return customerName.trim();
    return "";
  }, [bookingDetails, userNameFromUrl, customerName]);

  const paymentPayerMobile = useMemo(() => {
    const b = bookingDetails as Record<string, unknown> | null | undefined;
    const keys = [
      "phonenumber",
      "phoneno",
      "mobileNo",
      "mobileno",
      "mobile",
    ] as const;
    for (const k of keys) {
      const s = String(b?.[k] ?? "").trim();
      if (s && s !== "—") return s;
    }
    const m = String(state.mobileNo ?? "").trim();
    if (m && m !== "—") return m;
    if (phoneNumber && phoneNumber !== "—") return String(phoneNumber).trim();
    return "";
  }, [bookingDetails, state.mobileNo, phoneNumber]);

  useEffect(() => {
    if (bookingIdFromUrl) {
      const fetchDetails = async () => {
        setLoadingDetails(true);
        setTripStatusFromApi(undefined);
        bookingSelectionsAppliedRef.current = null;
        setIsEditingOldChecklist(false);
        setNewVehiclePhotos([]);
        setNewVehicleBatteryPhotos([]);
        setNewVehicleChargerPhotos([]);
        setNewVehicleChecklistMappings({});
        setCustomerKycState({
          loading: false,
          checked: false,
          verified: true,
          hasDlOcrFullname: true,
        });
        try {
          const res = (await apiService.getBookingById(
            bookingIdFromUrl,
          )) as any;
          const single = extractBookingDetailsFromApiResponse(res);
          if (single) setBookingDetails(single);
          else setBookingDetails(null);
          const status =
            (single as any)?.tripstatus ?? (single as any)?.tripStatus;
          setTripStatusFromApi(status != null ? Number(status) : undefined);
        } catch {
          setBookingDetails(null);
          setTripStatusFromApi(undefined);
        } finally {
          setLoadingDetails(false);
        }
      };
      fetchDetails();
    } else {
      setLoadingDetails(false);
      setTripStatusFromApi(undefined);
    }
  }, [bookingIdFromUrl]);

  useEffect(() => {
    const vid = bookingDetails?.vehicleid;
    if (vid == null || vid === "" || Number.isNaN(Number(vid))) {
      setBookingVehicleApiExtras(null);
      return;
    }
    let cancelled = false;
    apiService
      .getVehicleById(String(vid))
      .then((res: unknown) => {
        if (cancelled) return;
        const r = res as
          | { data?: unknown; result?: unknown }
          | null
          | undefined;
        const data =
          (r && typeof r === "object"
            ? ((r as { data?: unknown }).data ??
              (r as { result?: unknown }).result)
            : undefined) ?? r;
        if (!data || typeof data !== "object") {
          setBookingVehicleApiExtras({
            imageUrl: "",
            speed: "—",
            range: "—",
            hubMasterId: null,
            modelId: null,
            modelName: "",
          });
          return;
        }
        const d = data as Record<string, unknown>;
        const imageUrl = pickVehicleCardImageFromApiField(
          readVehicleImagesJsonFromRow(d),
        );
        const topspeed = d.topspeed;
        const rangeperchargekm = d.rangeperchargekm;
        const speed =
          topspeed === null || topspeed === undefined || topspeed === ""
            ? "—"
            : `${String(topspeed)} KM/H`;
        const range =
          rangeperchargekm === null ||
          rangeperchargekm === undefined ||
          rangeperchargekm === ""
            ? "—"
            : `${String(rangeperchargekm)} KM`;
        const rawHub = d.hubmasterid ?? d.hubId ?? d.hubid;
        const hubMasterId =
          rawHub === null || rawHub === undefined || rawHub === ""
            ? null
            : Number(rawHub);
        const hubMasterIdNorm =
          hubMasterId != null && Number.isFinite(hubMasterId)
            ? hubMasterId
            : null;
        const rawModelId =
          d.vehiclemodel ?? d.vehiclemodelid ?? d.vehicleModelId ?? d.modelid;
        const parsedModelId =
          rawModelId == null || rawModelId === ""
            ? Number.NaN
            : Number(rawModelId);
        const modelIdNorm = Number.isFinite(parsedModelId)
          ? parsedModelId
          : null;
        const modelNameRaw =
          d.vehiclemodelname ??
          d.vehicleModelName ??
          d.modelname ??
          d.modelName;
        const modelName = String(modelNameRaw ?? "").trim();
        setBookingVehicleApiExtras({
          imageUrl,
          speed,
          range,
          hubMasterId: hubMasterIdNorm,
          modelId: modelIdNorm,
          modelName,
        });
      })
      .catch(() => {
        if (!cancelled) setBookingVehicleApiExtras(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingDetails?.vehicleid]);

  useEffect(() => {
    const b = bookingDetails;
    if (!b) {
      setCustomerKycState({
        loading: false,
        checked: false,
        verified: true,
        hasDlOcrFullname: true,
      });
      return;
    }
    const fromBooking = pickCustomerUserIdForKyc(b);
    const fromNav =
      typeof state.renterUserId === "number" &&
      Number.isFinite(state.renterUserId) &&
      state.renterUserId > 0
        ? state.renterUserId
        : null;
    const userIdNum = fromBooking ?? fromNav;
    if (userIdNum == null) {
      setCustomerKycState({
        loading: false,
        checked: true,
        verified: true,
        hasDlOcrFullname: true,
      });
      return;
    }
    let cancelled = false;
    setCustomerKycState({
      loading: true,
      checked: false,
      verified: true,
      hasDlOcrFullname: true,
    });
    apiService
      .getKYCByUserId(String(userIdNum))
      .then((res: any) => {
        if (cancelled) return;
        const list = pickKycListFromResponse(res);
        const primary =
          list.find((k: any) => Number(k?.kycstatus) === 1) ??
          list[list.length - 1];
        const statusNum =
          primary != null ? Number(primary.kycstatus) : Number.NaN;
        const verified = statusNum === 1;
        const hasDlOcrFullname = kycDlOcrFullnamePresent(primary);
        setCustomerKycState({
          loading: false,
          checked: true,
          verified,
          hasDlOcrFullname,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCustomerKycState({
          loading: false,
          checked: true,
          verified: false,
          hasDlOcrFullname: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [bookingDetails, state.renterUserId]);

  useEffect(() => {
    if (!customerKycState.checked || customerKycState.loading) return;
    if (selectedVehicleId == null) return;
    const v = hubVehicles.find(
      (x: any) => String(x.vehicleid ?? x.id) === String(selectedVehicleId),
    );
    if (!v) return;
    const allowsOnePerson = customerKycState.verified;
    const allowsHighSpeed =
      customerKycState.verified && customerKycState.hasDlOcrFullname;
    const onePersonBlocked = Number(v.vehicletype) === 1 && !allowsOnePerson;
    const highSpeedBlocked = isHubVehicleHighSpeed(v) && !allowsHighSpeed;
    if (onePersonBlocked || highSpeedBlocked) setSelectedVehicleId(null);
  }, [
    customerKycState.checked,
    customerKycState.loading,
    customerKycState.verified,
    customerKycState.hasDlOcrFullname,
    selectedVehicleId,
    hubVehicles,
    isHubVehicleHighSpeed,
  ]);

  const bookingSelectionsAppliedRef = useRef<number | null>(null);

  useEffect(() => {
    const b = bookingDetails;
    const bookingId = b?.bookingmasterid ?? null;
    const apiVehicleId = b?.vehicleid;
    const apiPlanId = b?.planid;
    if (bookingId == null || (apiVehicleId == null && apiPlanId == null))
      return;
    if (bookingSelectionsAppliedRef.current === bookingId) return;

    const needVehicleSync = apiVehicleId != null;
    const needPlanSync = apiPlanId != null;
    if (needVehicleSync && hubVehicles.length === 0) return;
    if (needPlanSync && plansData.length === 0) return;

    if (needVehicleSync && hubVehicles.length > 0) {
      const vehicle = hubVehicles.find(
        (v: any) => Number(v.vehicleid ?? v.id) === Number(apiVehicleId),
      );
      if (vehicle) {
        const modelId = vehicle.vehiclemodel ?? vehicle.vehiclemodelid;
        setSelectedVehicleId(String(apiVehicleId));
        if (modelId != null) setSelectedModelId(String(modelId));
      }
    }

    if (needPlanSync && plansData.length > 0) {
      const planExists = plansData.some(
        (p: any) => Number(p.planid ?? p.id) === Number(apiPlanId),
      );
      if (planExists) setSelectedPlanId(String(apiPlanId));
    }

    bookingSelectionsAppliedRef.current = bookingId;
  }, [
    bookingDetails?.vehicleid,
    bookingDetails?.planid,
    bookingDetails?.bookingmasterid,
    hubVehicles,
    plansData,
  ]);

  useEffect(() => {
    if (activeStep !== 3) return;
    const uid = promoCustomerUserId != null ? Number(promoCustomerUserId) : NaN;
    if (!Number.isFinite(uid) || uid <= 0) {
      setPromoCodeOptions([]);
      setPromoCodeMetaByValue({});
      return;
    }
    let cancelled = false;
    setPromoCodesLoading(true);
    apiService
      .getPromoCodesByUserId(uid)
      .then((res) => {
        if (cancelled) return;
        const rows = pickKycListFromResponse(res);
        const seen = new Set<string>();
        const opts: { label: string; value: string }[] = [];
        const meta: Record<
          string,
          { couponid: number; couponName: string; isReferralCoupon: boolean }
        > = {};
        rows.forEach((raw, i) => {
          if (raw == null || typeof raw !== "object") return;
          const r = raw as Record<string, unknown>;
          const name = String(r.couponname ?? r.couponName ?? "").trim();
          const idRaw = r.couponid ?? r.couponId;
          const idStr =
            idRaw != null && String(idRaw).trim() !== "" ? String(idRaw) : "";
          const value = idStr || name || `promo-${i}`;
          if (seen.has(value)) return;
          seen.add(value);
          opts.push({ label: name || value, value });
          const idNum = Number.parseInt(String(idRaw ?? ""), 10);
          const couponid = Number.isFinite(idNum) ? idNum : 0;
          const referralRaw =
            r.isreferralcoupon ?? r.isReferralCoupon ?? r.IsReferralCoupon;
          const isReferralCoupon =
            referralRaw === true ||
            referralRaw === 1 ||
            String(referralRaw).toLowerCase() === "true";
          meta[value] = {
            couponid,
            couponName: name || value,
            isReferralCoupon,
          };
        });
        setPromoCodeOptions(opts);
        setPromoCodeMetaByValue(meta);
      })
      .catch(() => {
        if (!cancelled) {
          setPromoCodeOptions([]);
          setPromoCodeMetaByValue({});
        }
      })
      .finally(() => {
        if (!cancelled) setPromoCodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep, promoCustomerUserId]);

  useEffect(() => {
    if (selectedHubId !== null || !bookingDetails || !displayHubs?.length)
      return;

    const hubIdRaw = bookingDetails.hubmasterid ?? bookingDetails.hubid;
    if (hubIdRaw != null) {
      const numHubId = Number(hubIdRaw);
      if (
        Number.isFinite(numHubId) &&
        displayHubs.some((h) => Number(h.hubmasterid) === numHubId)
      ) {
        setSelectedHubId(numHubId);
        return;
      }
    }

    const name = String(bookingDetails.hubname ?? "").trim();
    if (name) {
      const lower = name.toLowerCase();
      const match = displayHubs.find(
        (h) =>
          String(h.hubname ?? "")
            .trim()
            .toLowerCase() === lower,
      );
      if (match) setSelectedHubId(Number(match.hubmasterid));
    }
  }, [bookingDetails, selectedHubId, displayHubs]);

  useEffect(() => {
    if (displayHubs?.length && selectedHubId !== null) {
      const existsInHubs = displayHubs.some(
        (h) => Number(h.hubmasterid) === Number(selectedHubId),
      );
      if (existsInHubs) return;
      setSelectedHubId(null);
    }
  }, [displayHubs, selectedHubId]);

  useEffect(() => {
    try {
      sessionStorage.setItem(stepStorageKey, String(activeStep));
    } catch {
      // ignore
    }
  }, [activeStep, stepStorageKey]);

  useEffect(() => {
    try {
      if (selectedHubId === null) sessionStorage.removeItem(hubIdStorageKey);
      else sessionStorage.setItem(hubIdStorageKey, String(selectedHubId));
    } catch {
      // ignore
    }
  }, [selectedHubId, hubIdStorageKey]);

  useEffect(() => {
    try {
      if (selectedModelId === null || selectedModelId === "")
        sessionStorage.removeItem(modelIdStorageKey);
      else sessionStorage.setItem(modelIdStorageKey, String(selectedModelId));
    } catch {
      // ignore
    }
  }, [selectedModelId, modelIdStorageKey]);

  useEffect(() => {
    try {
      if (selectedVehicleId === null || selectedVehicleId === "")
        sessionStorage.removeItem(vehicleIdStorageKey);
      else
        sessionStorage.setItem(vehicleIdStorageKey, String(selectedVehicleId));
    } catch {
      // ignore
    }
  }, [selectedVehicleId, vehicleIdStorageKey]);

  useEffect(() => {
    try {
      if (selectedPlanId === null || selectedPlanId === "")
        sessionStorage.removeItem(planIdStorageKey);
      else sessionStorage.setItem(planIdStorageKey, String(selectedPlanId));
    } catch {
      // ignore
    }
  }, [selectedPlanId, planIdStorageKey]);

  const handleCancel = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    } else {
      navigate("/booking-management", {
        state: state.filters
          ? { filters: state.filters, page: state.page }
          : undefined,
      });
    }
  };

  const fetchVehiclesForHub = async (hubId: number) => {
    setVehiclesLoading(true);
    try {
      const res = await apiService.getVehiclesByHubId({
        hubmasterid: hubId,
        pageNumber: 1,
        pageSize: 100,
      });
      const { items } = parseSearchPaymentDetailsResponse(res);
      setHubVehicles(Array.isArray(items) ? items : []);
    } catch {
      setHubVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const fetchPlansForHub = async (hubId: number) => {
    setPlansLoading(true);
    try {
      const res: any = await apiService.getPlansByHubId(String(hubId));
      const data = res?.data || res;
      setPlansData(Array.isArray(data) ? data : []);
    } catch {
      setPlansData([]);
    } finally {
      setPlansLoading(false);
    }
  };

  /** Load hub vehicles/plans whenever hub is known so booking vehicle & model can pre-select without advancing steps or refreshing */
  useEffect(() => {
    if (selectedHubId === null) return;
    fetchVehiclesForHub(selectedHubId);
    fetchPlansForHub(selectedHubId);
  }, [selectedHubId]);

  /**
   * ChangeVehicleStatusToUnderbooking — when API returns `status: 7`, vehicle is not available (e.g. already under booking).
   * Show the server `message` via NotificationContext (not a static string).
   */
  const trySelectVehicleAfterUnderbookingApi = useCallback(
    async (
      vehicleId: number,
      vehicle: Record<string, unknown>,
    ): Promise<boolean> => {
      const rawStatus =
        vehicle.status ?? vehicle.vehiclestatus ?? vehicle.vehicleStatus;
      if (rawStatus == null || rawStatus === "") return true;
      try {
        const res = (await apiService.updateVehicleStatus(
          vehicleId,
          Number(rawStatus),
        )) as Record<string, unknown> | null | undefined;
        const inner =
          res != null &&
          typeof res === "object" &&
          res.data != null &&
          typeof res.data === "object" &&
          !Array.isArray(res.data) &&
          ("status" in (res.data as object) ||
            "message" in (res.data as object))
            ? (res.data as { status?: unknown; message?: unknown })
            : res;
        if (
          inner != null &&
          typeof inner === "object" &&
          Number(inner.status) === 7
        ) {
          const msg =
            typeof inner.message === "string" ? inner.message.trim() : "";
          showNotification(msg);
          return false;
        }
        return true;
      } catch (e: unknown) {
        const fallback =
          e instanceof Error && e.message
            ? e.message
            : "Unable to reserve this vehicle.";
        showNotification(fallback, "error");
        return false;
      }
    },
    [showNotification],
  );

  /** Selection only; ChangeVehicleStatusToUnderbooking runs on Next (step 2 → 3). */
  const handleVehicleStepSelect = useCallback((id: string | number) => {
    setSelectedVehicleId(id);
  }, []);

  const {
    hasUpdateSection,
    hasNewVehicleSection,
    vehicleStepValid,
    originalVehicleType,
    selectedVehicleType,
    kycSelectionInvalid,
    kycBlocksOnePersonVehicles,
    kycBlocksHighSpeedVehicles,
    kycVehicleStepBlockMessage,
  } = useMemo(() => {
    const b = bookingDetails || {};
    const hasUpdate = tripStatusFromApi === 5;
    const hasTripStartData = Boolean(
      b.tripStart &&
      ((Array.isArray(b.tripStart.vehicleconditionchecklistmappings) &&
        b.tripStart.vehicleconditionchecklistmappings.length > 0) ||
        b.tripStart.vehiclephotos ||
        b.tripStart.batteryphotos ||
        b.tripStart.chargerphotos),
    );
    const hasUpdateChecklistSection = hasUpdate && hasTripStartData;
    const hasNew =
      selectedVehicleId != null &&
      b.vehicleid != null &&
      Number(selectedVehicleId) !== Number(b.vehicleid);
    /** Upload validation for new vehicle checklist only when trip status is 5 (same as Update checklist UI). */
    const newVehicleUploadRequired = hasNew && tripStatusFromApi === 5;
    const originalVehicle = hubVehicles?.find(
      (v: any) => Number(v.vehicleid ?? v.id) === Number(b.vehicleid),
    );
    const selectedVehicle = hubVehicles?.find(
      (v: any) => String(v.vehicleid ?? v.id) === String(selectedVehicleId),
    );
    const origType = originalVehicle?.vehicletype ?? null;
    const selType = selectedVehicle?.vehicletype ?? null;
    const hasUpdateChecklistSelection = Object.values(
      vehicleConditionChecklistMappings,
    ).includes(true);
    const hasNewChecklistSelection = Object.values(
      newVehicleChecklistMappings,
    ).includes(true);
    const updateOk =
      !hasUpdate ||
      !hasUpdateChecklistSection ||
      (hasUpdateChecklistSelection &&
        vehiclePhotos.length > 0 &&
        (origType !== 1 ||
          (batteryPhotos.length > 0 && chargerPhotos.length > 0)));
    const newOk =
      !newVehicleUploadRequired ||
      (hasNewChecklistSelection &&
        newVehiclePhotos.length > 0 &&
        (selType !== 1 ||
          (newVehicleBatteryPhotos.length > 0 &&
            newVehicleChargerPhotos.length > 0)));
    const selectedSpeedLabel =
      selectedVehicle != null
        ? String(
            getSpeedTypeDisplayName(
              selectedVehicle.speedtype ?? selectedVehicle.speedType,
            ) ?? "",
          ).toLowerCase()
        : "";
    const selectedIsHighSpeed = selectedSpeedLabel.includes("high");
    const kycBlocksOnePersonVehicles =
      customerKycState.checked && !customerKycState.verified;
    const kycBlocksHighSpeedVehicles =
      customerKycState.checked &&
      (!customerKycState.verified || !customerKycState.hasDlOcrFullname);
    const kycOnePersonInvalid =
      !customerKycState.loading &&
      kycBlocksOnePersonVehicles &&
      selectedVehicle != null &&
      Number(selectedVehicle?.vehicletype) === 1;
    const kycHighSpeedInvalid =
      !customerKycState.loading &&
      kycBlocksHighSpeedVehicles &&
      selectedVehicle != null &&
      selectedIsHighSpeed;
    const kycSelectionInvalid = kycOnePersonInvalid || kycHighSpeedInvalid;
    const kycVehicleStepBlockMessage = kycHighSpeedInvalid
      ? KYC_HIGH_SPEED_DL_MESSAGE
      : kycOnePersonInvalid
        ? KYC_INCOMPLETE_VEHICLE_MESSAGE
        : null;
    const valid = updateOk && newOk && !kycSelectionInvalid;
    return {
      hasUpdateSection: hasUpdateChecklistSection,
      hasNewVehicleSection: hasNew,
      vehicleStepValid: valid,
      originalVehicleType: origType,
      selectedVehicleType: selType,
      kycSelectionInvalid,
      kycBlocksOnePersonVehicles,
      kycBlocksHighSpeedVehicles,
      kycVehicleStepBlockMessage,
    };
  }, [
    bookingDetails,
    tripStatusFromApi,
    selectedVehicleId,
    hubVehicles,
    customerKycState.checked,
    customerKycState.verified,
    customerKycState.loading,
    customerKycState.hasDlOcrFullname,
    getSpeedTypeDisplayName,
    vehiclePhotos.length,
    batteryPhotos.length,
    chargerPhotos.length,
    vehicleConditionChecklistMappings,
    newVehiclePhotos.length,
    newVehicleBatteryPhotos.length,
    newVehicleChargerPhotos.length,
    newVehicleChecklistMappings,
  ]);

  const REASON_REQUIRED_MESSAGE = "Select reason is required.";
  const OTHER_REMARKS_REQUIRED_MESSAGE =
    "Please enter remarks when Other is selected.";

  const handleNext = async () => {
    if (activeStep === 0) {
      const reasonTrimmed = typeof reason === "string" ? reason.trim() : "";
      if (reasonTrimmed === "") {
        setError(REASON_REQUIRED_MESSAGE);
        return;
      }
      if (
        updateVehicleRemarksOtherCodeValue !== "" &&
        reasonTrimmed === updateVehicleRemarksOtherCodeValue &&
        remarks.trim() === ""
      ) {
        setError(OTHER_REMARKS_REQUIRED_MESSAGE);
        return;
      }
    }
    if (activeStep === 1 && kycModelSelectionInvalid) {
      setError(KYC_HIGH_SPEED_DL_MESSAGE);
      return;
    }
    if (activeStep === 2 && !vehicleStepValid) {
      setShowVehicleUploadValidation(true);
      let message: string;
      const hasUpdateChecklistSelection = Object.values(
        vehicleConditionChecklistMappings,
      ).includes(true);
      const hasNewChecklistSelection = Object.values(
        newVehicleChecklistMappings,
      ).includes(true);
      if (kycSelectionInvalid) {
        message = kycVehicleStepBlockMessage ?? KYC_INCOMPLETE_VEHICLE_MESSAGE;
      } else if (hasUpdateSection && !hasUpdateChecklistSelection) {
        message =
          "Select at least one item in Update existing vehicle checklist before proceeding.";
      } else if (hasUpdateSection && vehiclePhotos.length === 0) {
        message =
          "Vehicle images are required. Please upload at least one vehicle image before proceeding.";
      } else if (
        hasUpdateSection &&
        originalVehicleType === 1 &&
        (batteryPhotos.length === 0 || chargerPhotos.length === 0)
      ) {
        message =
          "Battery and charger images are required for this vehicle. Please upload at least one of each before proceeding.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        !hasNewChecklistSelection
      ) {
        message =
          "Select at least one item in New vehicle checklist before proceeding.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        newVehiclePhotos.length === 0
      ) {
        message =
          "Vehicle images are required for the new vehicle. Please upload at least one vehicle image before proceeding.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        selectedVehicleType === 1 &&
        (newVehicleBatteryPhotos.length === 0 ||
          newVehicleChargerPhotos.length === 0)
      ) {
        message =
          "Battery and charger images are required for the new vehicle. Please upload at least one of each before proceeding.";
      } else {
        message =
          "Please upload all required vehicle images before proceeding.";
      }
      setError(message);
      return;
    }
    if (activeStep === 2) {
      const vehicleId = Number(selectedVehicleId);
      const vehicle = hubVehicles.find(
        (v: any) => Number(v.vehicleid ?? v.id) === vehicleId,
      ) as Record<string, unknown> | undefined;
      if (Number.isFinite(vehicleId) && vehicle) {
        setVehicleUnderbookingLoading(true);
        try {
          const ok = await trySelectVehicleAfterUnderbookingApi(
            vehicleId,
            vehicle,
          );
          if (!ok) return;
        } finally {
          setVehicleUnderbookingLoading(false);
        }
      }
    }
    if (activeStep === 2) setShowVehicleUploadValidation(false);
    setError(null);
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (activeStep !== 2) setShowVehicleUploadValidation(false);
  }, [activeStep]);

  useEffect(() => {
    if (activeStep === 2 && vehicleStepValid) {
      setShowVehicleUploadValidation(false);
      if (error != null) setError(null);
    }
  }, [activeStep, vehicleStepValid, error]);

  const handleHubSelect = (hubId: number) => {
    setSelectedHubId(hubId);
  };

  const handleNavigate = (hub: {
    hubname: string;
    hubmasterid: number;
    latitude?: number;
    longitude?: number;
  }) => {
    if (hub.latitude != null && hub.longitude != null) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${hub.latitude},${hub.longitude}`,
        "_blank",
      );
    } else {
      const query = encodeURIComponent(hub.hubname);
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${query}`,
        "_blank",
      );
    }
  };

  const hubCards = useMemo(() => {
    return displayHubs.map((hub) => {
      const addressParts = [hub.address1, hub.address2, hub.zipcode].filter(
        Boolean,
      );
      const address = addressParts.length > 0 ? addressParts.join(", ") : "—";
      const active = Number(hub.activeVehicleCount ?? 0);
      const available = Number(hub.availableVehicleCount ?? 0);
      const totalVehicles = Number(hub.totalvehiclesCount ?? 0);
      const readyOrListed = active + available;
      const availableText =
        totalVehicles > 0
          ? `${readyOrListed}/${totalVehicles} Available`
          : `${readyOrListed} Available`;
      const rawPath = (hub.hubimage || "").trim().replace(/^\/+/, "");
      const imageUrl = rawPath ? `${BASE_IMAGE_URL}${rawPath}` : "";
      return {
        hubmasterid: hub.hubmasterid,
        hubname: hub.hubname,
        address,
        operationhours: hub.operationhours || "—",
        available: availableText,
        imageUrl,
        latitude: hub.latitude,
        longitude: hub.longitude,
      };
    });
  }, [displayHubs]);

  const vehicleModelOptions: VehicleModelOption[] = useMemo(() => {
    if (!hubVehicles?.length) return [];
    const vehicleTypeToSetting = (
      vehicletype: number | null | undefined,
    ): string => {
      if (vehicletype === 1) return "1 Person";
      if (vehicletype === 2) return "2 Person";
      return "—";
    };
    const byModel = new Map<number, any[]>();
    for (const v of hubVehicles) {
      const modelId = v.vehiclemodel ?? v.vehiclemodelid;
      if (modelId == null) continue;
      const key = Number(modelId);
      if (!byModel.has(key)) byModel.set(key, []);
      byModel.get(key)!.push(v);
    }
    return Array.from(byModel.entries()).map(([modelId, vehicles]) => {
      const first = vehicles[0];
      const speedDisplay = getSpeedTypeDisplayName(first.speedtype);
      const speed = speedDisplay === "" ? "—" : speedDisplay;
      const speedTypeLabel = String(speedDisplay ?? "").toLowerCase();
      const isHighSpeed = speedTypeLabel.includes("high");
      const weight =
        first.weight != null && first.weight !== ""
          ? `${first.weight} KG`
          : "—";
      const setting = vehicleTypeToSetting(first.vehicletype);
      const count = vehicles.length;
      const available = count === 1 ? "1 Available" : `${count} Available`;
      const modelDisplayName = getVehicleModelDisplayName(
        first.vehiclemodel ?? modelId,
      );
      const name =
        modelDisplayName === ""
          ? first.vehiclename || `Model ${modelId}`
          : modelDisplayName;
      return {
        id: modelId,
        name,
        speed,
        weight,
        setting,
        available,
        imageUrl: pickVehicleCardImageFromApiField(
          readVehicleImagesJsonFromRow(first),
        ),
        isHighSpeed,
      };
    });
  }, [
    hubVehicles,
    vehicleModelCodes,
    evSpeedTypeCodes,
    getVehicleModelDisplayName,
    getSpeedTypeDisplayName,
  ]);

  const kycModelSelectionInvalid = useMemo(() => {
    if (customerKycState.loading) return false;
    const kycBlocksHighSpeedModels =
      customerKycState.checked &&
      (!customerKycState.verified || !customerKycState.hasDlOcrFullname);
    if (!kycBlocksHighSpeedModels) return false;
    const opt = vehicleModelOptions.find(
      (o) => String(o.id) === String(selectedModelId),
    );
    return opt?.isHighSpeed === true;
  }, [
    customerKycState.loading,
    customerKycState.checked,
    customerKycState.verified,
    customerKycState.hasDlOcrFullname,
    selectedModelId,
    vehicleModelOptions,
  ]);

  useEffect(() => {
    if (!customerKycState.checked || customerKycState.loading) return;
    if (selectedModelId == null || selectedModelId === "") return;
    const opt = vehicleModelOptions.find(
      (o) => String(o.id) === String(selectedModelId),
    );
    if (opt?.isHighSpeed !== true) return;
    const allowsHighSpeed =
      customerKycState.verified && customerKycState.hasDlOcrFullname;
    if (!allowsHighSpeed) setSelectedModelId(null);
  }, [
    customerKycState.checked,
    customerKycState.loading,
    customerKycState.verified,
    customerKycState.hasDlOcrFullname,
    selectedModelId,
    vehicleModelOptions,
  ]);

  const selectedVehicleModelDisplayName = useMemo(() => {
    if (selectedModelId == null || selectedModelId === "") return "";
    const opt = vehicleModelOptions.find(
      (o) => String(o.id) === String(selectedModelId),
    );
    return (opt?.name ?? "").trim();
  }, [selectedModelId, vehicleModelOptions]);

  /** From GetBookingById (+ hub vehicles for model name) — shown on vehicle model & plan steps */
  const bookingVehiclePlanSummary = useMemo(() => {
    const b = bookingDetails;
    if (!b || b.bookingmasterid == null) return null;

    const vid = b.vehicleid;
    let modelName = "";
    if (vid != null && hubVehicles.length > 0) {
      const v = hubVehicles.find(
        (x: any) => Number(x.vehicleid ?? x.id) === Number(vid),
      );
      if (v) {
        const mid = v.vehiclemodel ?? v.vehiclemodelid;
        if (mid != null) {
          const dn = getVehicleModelDisplayName(mid);
          modelName = (
            dn && String(dn).trim() !== "" ? dn : `Model ${mid}`
          ).trim();
        }
      }
    }
    if (!modelName && b.vehiclemodel != null && b.vehiclemodel !== "") {
      const dn = getVehicleModelDisplayName(b.vehiclemodel);
      modelName = (
        dn && String(dn).trim() !== "" ? dn : String(b.vehiclemodel)
      ).trim();
    }
    if (!modelName && bookingVehicleApiExtras?.modelId != null) {
      const dn = getVehicleModelDisplayName(bookingVehicleApiExtras.modelId);
      modelName = (
        dn && String(dn).trim() !== ""
          ? dn
          : `Model ${bookingVehicleApiExtras.modelId}`
      ).trim();
    }
    if (!modelName && bookingVehicleApiExtras?.modelName) {
      modelName = bookingVehicleApiExtras.modelName.trim();
    }

    const vehicleName = String(b.vehiclename ?? "").trim();
    const vehicleNum = String(b.vehiclenum ?? "").trim();
    const registrationNumber = String(
      b.registrationnumber ?? b.registrationNumber ?? "",
    ).trim();
    const planName = String(b.planname ?? "").trim();

    const rawBookingAmount = b.bookingamount ?? b.bookingAmount;
    const bookingAmountParsed =
      rawBookingAmount != null && rawBookingAmount !== ""
        ? Number(rawBookingAmount)
        : NaN;
    const bookingAmount = Number.isFinite(bookingAmountParsed)
      ? bookingAmountParsed
      : null;

    const planStartRaw = b.planstartdate ?? b.planStartDate;
    const planStartDate =
      planStartRaw != null && String(planStartRaw).trim() !== ""
        ? String(planStartRaw)
        : null;
    const planEndRaw = b.planenddate ?? b.planEndDate;
    const planEndDate =
      planEndRaw != null && String(planEndRaw).trim() !== ""
        ? String(planEndRaw)
        : null;

    const api = bookingVehicleApiExtras;
    const bookingVehicleImageUrl = api?.imageUrl ?? "";
    const bookingVehicleSpeed = api?.speed ?? "—";
    const bookingVehicleRange = api?.range ?? "—";
    const hid = api?.hubMasterId;
    let vehicleHubName = "—";
    if (hid != null && Number.isFinite(hid)) {
      const fromDisplay = displayHubs.find(
        (h) => Number(h.hubmasterid) === Number(hid),
      );
      if (fromDisplay?.hubname)
        vehicleHubName = String(fromDisplay.hubname).trim() || "—";
      else {
        const fromAll = (hubs ?? []).find(
          (h) => Number(h.hubmasterid) === Number(hid),
        );
        if (fromAll?.hubname)
          vehicleHubName = String(fromAll.hubname).trim() || "—";
      }
    }

    return {
      modelName: modelName || "—",
      vehicleName: vehicleName || "—",
      vehicleNum: vehicleNum || "—",
      registrationNumber: registrationNumber || "—",
      planName: planName || "—",
      imageUrl: bookingVehicleImageUrl,
      speed: bookingVehicleSpeed,
      range: bookingVehicleRange,
      hubName: vehicleHubName,
      bookingAmount,
      planStartDate,
      planEndDate,
    };
  }, [
    bookingDetails,
    hubVehicles,
    getVehicleModelDisplayName,
    bookingVehicleApiExtras,
    displayHubs,
    hubs,
  ]);

  const noPlanChangeWindowDisplay = useMemo(() => {
    const ps = bookingVehiclePlanSummary?.planStartDate;
    const pe = bookingVehiclePlanSummary?.planEndDate;
    if (
      ps == null ||
      String(ps).trim() === "" ||
      pe == null ||
      String(pe).trim() === ""
    )
      return null;
    const start = DATE_FORMATS.formatDate(String(ps).trim());
    const end = DATE_FORMATS.formatDate(String(pe).trim());
    if (!start && !end) return null;
    return {
      start: start || String(ps).trim(),
      end: end || String(pe).trim(),
    };
  }, [
    bookingVehiclePlanSummary?.planStartDate,
    bookingVehiclePlanSummary?.planEndDate,
  ]);

  const handleEditBookingSelectPlan = useCallback((id: string | number) => {
    setOnlyVehicleChangeNoPlan(false);
    setSelectedPlanId(id);
  }, []);

  const handleNoPlanChangeClick = useCallback(() => {
    setSelectedPlanId(null);
    setAppliedCoupon(null);
    setCouponCode("");
    setOnlyVehicleChangeNoPlan(true);
  }, []);

  useEffect(() => {
    if (activeStep !== 3) setOnlyVehicleChangeNoPlan(false);
  }, [activeStep]);

  const vehicleOptions: VehicleOption[] = useMemo(() => {
    if (!hubVehicles.length) return [];
    const byModel =
      selectedModelId === null || selectedModelId === undefined
        ? hubVehicles
        : hubVehicles.filter(
            (v: any) =>
              String(v.vehiclemodel || v.vehiclemodelid) ===
              String(selectedModelId),
          );
    const allowedStatuses = [1, 5];
    const filtered = byModel.filter((v: any) => {
      const status = v.status ?? v.vehiclestatus ?? v.vehicleStatus;
      const num = status != null && status !== "" ? Number(status) : NaN;
      return Number.isFinite(num) && allowedStatuses.includes(num);
    });
    return filtered.map((v: any) => {
      const speedTypeLabel = String(
        getSpeedTypeDisplayName(v.speedtype ?? v.speedType) ?? "",
      ).toLowerCase();
      return {
        id: v.vehicleid ?? v.id,
        name: v.vehiclename,
        speed:
          v.topspeed === null || v.topspeed === undefined
            ? "—"
            : `${v.topspeed} KM/H`,
        range:
          v.rangeperchargekm === null || v.rangeperchargekm === undefined
            ? "—"
            : `${v.rangeperchargekm} KM`,
        imageUrl: pickVehicleCardImageFromApiField(
          readVehicleImagesJsonFromRow(v),
        ),
        vehicleNum: v.vehiclenum || "",
        registrationNumber: String(
          v.registrationnumber ??
            v.registrationNumber ??
            v.vechileistrationnumber ??
            v.vehicleRegistrationNumber ??
            "",
        ).trim(),
        vehicleType:
          v.vehicletype != null && v.vehicletype !== ""
            ? Number(v.vehicletype)
            : null,
        isHighSpeed: speedTypeLabel.includes("high"),
      };
    });
  }, [hubVehicles, selectedModelId, getSpeedTypeDisplayName]);

  /** Selected hub vehicle — shown under "Your selection" on plan step (empty → N/A in UI) */
  const yourSelectionVehicleDetails: YourSelectionVehicleDetailStrings =
    useMemo(() => {
      if (selectedVehicleId == null || selectedVehicleId === "") {
        return {
          vehicleName: "",
          vehicleNum: "",
          registrationNumber: "",
          speed: "",
          range: "",
        };
      }
      const v = vehicleOptions.find(
        (x) => String(x.id) === String(selectedVehicleId),
      );
      if (!v) {
        return {
          vehicleName: "",
          vehicleNum: "",
          registrationNumber: "",
          speed: "",
          range: "",
        };
      }
      return {
        vehicleName: String(v.name ?? "").trim(),
        vehicleNum: String(v.vehicleNum ?? "").trim(),
        registrationNumber: String(v.registrationNumber ?? "").trim(),
        speed: String(v.speed ?? "").trim(),
        range: String(v.range ?? "").trim(),
      };
    }, [selectedVehicleId, vehicleOptions]);

  const planOptions: PlanOption[] = useMemo(() => {
    if (selectedModelId == null || selectedModelId === "") return [];
    const filtered = plansData.filter((p: any) => {
      const planModelId = p.vehiclemodelid ?? p.vehiclemodel;
      if (
        planModelId === null ||
        planModelId === undefined ||
        planModelId === ""
      )
        return false;
      return String(planModelId) === String(selectedModelId);
    });
    return filtered.map((p: any) => ({
      id: p.planid ?? p.id,
      name: p.planname || " ",
      duration: p.days ? `${p.days} Days` : undefined,
      price:
        p.price === null || p.price === undefined
          ? undefined
          : `₹${Number(p.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      isCustom: false,
    }));
  }, [plansData, selectedModelId]);

  /**
   * Plan window sent in `bookingPlanUpdateModel` (today → inclusive end from selected plan `days`).
   * Reused on Select Plan step Your selection for "New plan start" / "New plan end".
   * Adds one calendar day to the end when (a) the loaded booking spans more inclusive days than its
   * catalog plan `days` (e.g. weekly 7 + create post-2PM extension → 8 days), or (b) IST is after 2 PM
   * (parity with create booking).
   */
  const newPlanWindowForEdit = useMemo(() => {
    if (selectedPlanId == null || selectedPlanId === "") return null;
    const selectedPlan = plansData.find(
      (p: any) => String(p.planid ?? p.id) === String(selectedPlanId),
    );
    const rawDays = Number(selectedPlan?.days);
    const planDurationDays = Math.max(
      1,
      Number.isFinite(rawDays) && rawDays > 0 ? rawDays : 1,
    );
    const planStart = dayjs().startOf("day");
    let planEnd = planStart.add(planDurationDays - 1, "day").startOf("day");

    const b = bookingDetails;
    const oldPlanRow =
      b != null && b.planid != null && String(b.planid).trim() !== ""
        ? plansData.find(
            (p: any) => String(p.planid ?? p.id) === String(b.planid),
          )
        : undefined;
    const nominalCurrentPlanDaysRaw = Number(
      oldPlanRow?.days ??
        (b as any)?.days ??
        (b as any)?.planDays ??
        (b as any)?.plandays,
    );
    const nominalCurrentPlanDays =
      Number.isFinite(nominalCurrentPlanDaysRaw) &&
      nominalCurrentPlanDaysRaw > 0
        ? nominalCurrentPlanDaysRaw
        : NaN;

    const rawPs =
      bookingVehiclePlanSummary?.planStartDate ??
      b?.planstartdate ??
      (b as any)?.planStartDate ??
      null;
    const rawPe =
      bookingVehiclePlanSummary?.planEndDate ??
      b?.planenddate ??
      (b as any)?.planEndDate ??
      null;
    const ps =
      rawPs != null && String(rawPs).trim() !== ""
        ? String(rawPs).trim()
        : null;
    const pe =
      rawPe != null && String(rawPe).trim() !== ""
        ? String(rawPe).trim()
        : null;

    const inclusiveCurrent = countInclusivePlanCalendarDays(ps, pe);
    const currentBookingHadExtraCalendarDay =
      Number.isFinite(nominalCurrentPlanDays) &&
      inclusiveCurrent > 0 &&
      inclusiveCurrent > nominalCurrentPlanDays;

    if (currentBookingHadExtraCalendarDay || isIndianWallClockAfterTwoPm()) {
      planEnd = planEnd.add(1, "day").startOf("day");
    }

    return {
      planStartISO: planStart.toISOString(),
      planEndISO: planEnd.toISOString(),
      planStartDisplay: planStart.format("DD/MM/YYYY"),
      planEndDisplay: planEnd.format("DD/MM/YYYY"),
    };
  }, [
    selectedPlanId,
    plansData,
    bookingDetails,
    bookingVehiclePlanSummary?.planStartDate,
    bookingVehiclePlanSummary?.planEndDate,
  ]);

  /** Clear plan selection if it does not belong to the selected vehicle model (after plans load) */
  useEffect(() => {
    if (selectedPlanId == null || selectedPlanId === "") return;
    if (plansLoading) return;
    const stillValid = planOptions.some(
      (p) => String(p.id) === String(selectedPlanId),
    );
    if (!stillValid) setSelectedPlanId(null);
  }, [planOptions, selectedPlanId, plansLoading]);

  useEffect(() => {
    if (activeStep !== 3) {
      setCostBreakdownLoading(false);
      return;
    }

    const b = bookingDetails || {};
    const bookingMasterId =
      b.bookingmasterid != null ? Number(b.bookingmasterid) : 0;
    const oldPlanId = b.planid != null ? Number(b.planid) : 0;
    const oldBookingPlanId =
      b.bookingplanid != null ? Number(b.bookingplanid) : 0;
    const newPlanId = selectedPlanId != null ? Number(selectedPlanId) : 0;

    const canFetchVehicleOnly =
      onlyVehicleChangeNoPlan &&
      bookingMasterId > 0 &&
      oldBookingPlanId > 0 &&
      oldPlanId > 0;
    const canFetchPlanChange =
      !onlyVehicleChangeNoPlan &&
      bookingMasterId > 0 &&
      oldBookingPlanId > 0 &&
      newPlanId > 0;

    if (!canFetchVehicleOnly && !canFetchPlanChange) {
      setCostBreakdownLoading(false);
      setCostBreakdown([]);
      setTotalPayable("₹0.00");
      setCostBreakdownRaw({});
      setCashPaymentCharge("");
      setLateFeesBaseline(0);
      setLateFeesAmount("");
      return;
    }

    let cancelled = false;
    setCostBreakdownLoading(true);
    const formatRs = (n: number) =>
      `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

    const applyBreakdown = (
      raw: Record<string, unknown>,
      cashFeeNum: number,
    ) => {
      const lateFromApi = Number(raw.latefees ?? raw.lateFees ?? 0);
      const lateSafe =
        Number.isFinite(lateFromApi) && lateFromApi >= 0 ? lateFromApi : 0;
      setLateFeesBaseline(lateSafe);
      setLateFeesAmount(String(lateSafe));
      const breakdown: CostBreakdownItem[] = [
        {
          label: "Plan price",
          value: formatRs(Number(raw.totalplanprice ?? 0)),
          message: raw.planmessage as string | undefined,
        },
        {
          label: "Processing fees",
          value: formatRs(Number(raw.totalalprocessingfees ?? 0)),
          message: raw.planprocessingfeesmessage as string | undefined,
        },
        {
          label: "Deposit amount",
          value: formatRs(Number(raw.totalrefundableamount ?? 0)),
          message: raw.planrefundableamountmessage as string | undefined,
        },
        { label: "Late fees", value: formatRs(lateSafe) },
        { label: "Cash Handling Fees", value: formatRs(cashFeeNum) },
      ];
      const total = Number(raw.totalpayableamount ?? 0);
      setCostBreakdown(breakdown);
      setTotalPayable(total > 0 ? formatRs(total) : "₹0.00");
      setCostBreakdownRaw({
        planprice: Number(raw.totalplanprice ?? 0),
        processing_fees: Number(raw.totalalprocessingfees ?? 0),
        refundable_amount: Number(raw.totalrefundableamount ?? 0),
        planmessage:
          typeof raw.planmessage === "string"
            ? raw.planmessage
            : String(raw.planmessage ?? ""),
        planrefundableamountmessage:
          typeof raw.planrefundableamountmessage === "string"
            ? raw.planrefundableamountmessage
            : String(raw.planrefundableamountmessage ?? ""),
      });
    };

    (async () => {
      try {
        const res: any = await apiService.getCostBreakdownDifference({
          oldplanid: oldPlanId,
          newplanid: onlyVehicleChangeNoPlan ? oldPlanId : newPlanId,
          bookingmasterid: bookingMasterId,
          oldbookingplanid: oldBookingPlanId,
          onlyvehiclechange: onlyVehicleChangeNoPlan,
        });
        if (cancelled) return;
        const raw = res?.data ?? res?.result ?? res;
        if (!raw || typeof raw !== "object") {
          setCostBreakdown([]);
          setTotalPayable("₹0.00");
          setCostBreakdownRaw({});
          setCashPaymentCharge("");
          setLateFeesBaseline(0);
          setLateFeesAmount("");
          return;
        }
        const pp = Number(raw.totalplanprice ?? 0);
        const pf = Number(raw.totalalprocessingfees ?? 0);
        const ra = Number(raw.totalrefundableamount ?? 0);
        const allCoreNonPositive = pp <= 0 && pf <= 0 && ra <= 0;

        if (allCoreNonPositive) {
          setCashPaymentCharge("0");
          applyBreakdown(raw, 0);
          return;
        }

        let cashFeeNum = 0;
        try {
          const settingsRes: any = await apiService.getSystemSettingsData();
          if (cancelled) return;
          const data = settingsRes?.data ?? settingsRes?.result ?? settingsRes;
          const list = Array.isArray(data) ? data : [];
          const cash = list.find(
            (s: any) => s?.systemsettingskey === "CashPaymentCharge",
          );
          if (
            cash?.systemsettingsvalue != null &&
            String(cash.systemsettingsvalue).trim() !== ""
          ) {
            cashFeeNum = Number(cash.systemsettingsvalue) || 0;
          }
        } catch {
          cashFeeNum = 0;
        }
        if (cancelled) return;
        setCashPaymentCharge(String(cashFeeNum));
        applyBreakdown(raw, cashFeeNum);
      } catch {
        if (!cancelled) {
          setCostBreakdown([]);
          setTotalPayable("₹0.00");
          setCostBreakdownRaw({});
          setCashPaymentCharge("");
          setLateFeesBaseline(0);
          setLateFeesAmount("");
        }
      } finally {
        if (!cancelled) setCostBreakdownLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeStep,
    bookingDetails?.bookingmasterid,
    bookingDetails?.planid,
    bookingDetails?.bookingplanid,
    selectedPlanId,
    onlyVehicleChangeNoPlan,
  ]);

  const costBreakdownForSelectPlan = useMemo(() => {
    const formatRs = (n: number) =>
      `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    const addStr = additionalFees.trim();
    const addNum = addStr === "" ? Number.NaN : Number.parseFloat(addStr);
    const hasAdd = Number.isFinite(addNum) && addNum !== 0;

    const discNum =
      appliedCoupon?.discountValue != null
        ? Number(appliedCoupon.discountValue)
        : Number.NaN;
    const hasDisc = Number.isFinite(discNum) && discNum > 0;
    if (!hasAdd && !hasDisc) return costBreakdown;

    let copy = costBreakdown.filter(
      (x) =>
        !/^Discount\b/i.test(String(x.label)) && x.label !== "Additional fees",
    );

    if (hasDisc && appliedCoupon) {
      const depositIdx = copy.findIndex((x) => x.label === "Deposit amount");
      const label = `Discount (${appliedCoupon.name || appliedCoupon.code})`;
      const row: CostBreakdownItem = {
        label,
        value: `-₹${Number(discNum).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        isDiscount: true,
      };
      if (depositIdx >= 0) copy.splice(depositIdx + 1, 0, row);
      else copy.push(row);
    }

    if (hasAdd) {
      const idx = copy.findIndex((x) => x.label === "Cash Handling Fees");
      const row: CostBreakdownItem = {
        label: "Additional fees",
        value: formatRs(addNum),
      };
      if (idx >= 0) copy.splice(idx + 1, 0, row);
      else copy.push(row);
    }

    return copy;
  }, [costBreakdown, additionalFees, appliedCoupon]);

  const handleApplyCoupon = useCallback(async () => {
    if (onlyVehicleChangeNoPlan) {
      showNotification(
        "Choose a plan from the list to apply a coupon, or keep No Plan change without a coupon.",
        "error",
        COUPON_APPLY_NOTIFY_MS,
      );
      return;
    }
    if (
      selectedPlanId == null ||
      selectedPlanId === "" ||
      String(selectedPlanId).trim() === ""
    ) {
      showNotification("Select a plan first.", "error", COUPON_APPLY_NOTIFY_MS);
      return;
    }
    const code = couponCode.trim();
    if (!code) {
      showNotification(
        "Select a coupon from the list.",
        "error",
        COUPON_APPLY_NOTIFY_MS,
      );
      return;
    }
    const label =
      promoCodeOptions.find((o) => String(o.value) === code)?.label ?? code;
    const meta = promoCodeMetaByValue[code];
    const planPriceRaw = Number(costBreakdownRaw.planprice);
    const planprice = Number.isFinite(planPriceRaw) ? planPriceRaw : 0;
    const userid =
      pickCustomerUserIdForKyc(bookingDetails) ??
      (typeof state.renterUserId === "number" &&
      Number.isFinite(state.renterUserId) &&
      state.renterUserId > 0
        ? state.renterUserId
        : 0);
    const payload = {
      couponid: meta?.couponid ?? 0,
      couponName: meta?.couponName ?? label,
      isReferralCoupon: meta?.isReferralCoupon ?? false,
      planprice,
      userid,
    };
    if (!payload.couponName.trim()) {
      showNotification(
        "Invalid coupon selection.",
        "error",
        COUPON_APPLY_NOTIFY_MS,
      );
      return;
    }
    setCouponApplyLoading(true);
    try {
      const res = await apiService.checkCouponValidity(payload);
      if (couponValidityIndicatesFailure(res)) {
        const msg =
          readCouponValidityMessage(res) ?? "Coupon could not be applied.";
        showNotification(msg, "error", COUPON_APPLY_NOTIFY_MS);
        setAppliedCoupon(null);
        lastCouponValidityKeyRef.current = "";
        return;
      }
      const discountValue = readCouponDiscountValue(res);
      const couponNameFromApi = readCouponDisplayName(res);
      setAppliedCoupon({
        code,
        couponid: meta?.couponid ?? 0,
        name: couponNameFromApi || payload.couponName,
        discountValue,
        isReferralCoupon: meta?.isReferralCoupon ?? false,
      });
      lastCouponValidityKeyRef.current = `${code}|${String(selectedPlanId)}|${planprice}`;
      showNotification(
        `Coupon "${payload.couponName}" applied.`,
        "success",
        COUPON_APPLY_NOTIFY_MS,
      );
    } catch (e: unknown) {
      let msg = e instanceof Error ? e.message.trim() : "";
      if (!msg && e && typeof e === "object" && "response" in e) {
        const data = (e as { response?: { data?: unknown } }).response?.data;
        msg = readCouponValidityMessage(data) ?? "";
      }
      setAppliedCoupon(null);
      lastCouponValidityKeyRef.current = "";
      showNotification(
        msg || "Could not validate coupon.",
        "error",
        COUPON_APPLY_NOTIFY_MS,
      );
    } finally {
      setCouponApplyLoading(false);
    }
  }, [
    bookingDetails,
    couponCode,
    costBreakdownRaw.planprice,
    onlyVehicleChangeNoPlan,
    promoCodeMetaByValue,
    promoCodeOptions,
    selectedPlanId,
    showNotification,
    state.renterUserId,
  ]);

  useEffect(() => {
    if (!appliedCoupon) return;
    const current = couponCode.trim();
    if (current !== appliedCoupon.code) {
      setAppliedCoupon(null);
    }
  }, [couponCode, appliedCoupon]);

  useEffect(() => {
    if (activeStep !== 3) return;
    if (onlyVehicleChangeNoPlan) return;
    if (!appliedCoupon) {
      lastCouponValidityKeyRef.current = "";
      return;
    }
    if (
      selectedPlanId == null ||
      selectedPlanId === "" ||
      String(selectedPlanId).trim() === ""
    )
      return;
    if (costBreakdownLoading) return;
    const planprice = Number(costBreakdownRaw.planprice);
    if (!Number.isFinite(planprice)) return;

    const code = appliedCoupon.code;
    const meta = promoCodeMetaByValue[code];
    const key = `${code}|${String(selectedPlanId)}|${planprice}`;
    if (key === lastCouponValidityKeyRef.current) return;

    const userid =
      pickCustomerUserIdForKyc(bookingDetails) ??
      (typeof state.renterUserId === "number" &&
      Number.isFinite(state.renterUserId) &&
      state.renterUserId > 0
        ? state.renterUserId
        : 0);
    if (!Number.isFinite(userid) || userid <= 0) return;

    const couponName =
      appliedCoupon.name.trim() !== ""
        ? appliedCoupon.name
        : (meta?.couponName ?? code);
    const payload = {
      couponid: meta?.couponid ?? appliedCoupon.couponid ?? 0,
      couponName,
      isReferralCoupon: appliedCoupon.isReferralCoupon,
      planprice,
      userid,
    };
    if (!payload.couponName.trim()) return;

    const flightId = ++couponPlanRevalidateFlightRef.current;
    let cancelled = false;
    setCouponApplyLoading(true);
    (async () => {
      try {
        const res = await apiService.checkCouponValidity(payload);
        if (cancelled || flightId !== couponPlanRevalidateFlightRef.current)
          return;
        if (couponValidityIndicatesFailure(res)) {
          const msg =
            readCouponValidityMessage(res) ??
            "Coupon could not be applied for this plan.";
          showNotification(msg, "error", COUPON_APPLY_NOTIFY_MS);
          setAppliedCoupon(null);
          lastCouponValidityKeyRef.current = "";
          return;
        }
        const discountValue = readCouponDiscountValue(res);
        const couponNameFromApi = readCouponDisplayName(res);
        lastCouponValidityKeyRef.current = key;
        setAppliedCoupon({
          code,
          couponid: meta?.couponid ?? appliedCoupon.couponid ?? 0,
          name: couponNameFromApi || payload.couponName,
          discountValue,
          isReferralCoupon: appliedCoupon.isReferralCoupon,
        });
      } catch (e: unknown) {
        if (cancelled || flightId !== couponPlanRevalidateFlightRef.current)
          return;
        let msg = e instanceof Error ? e.message.trim() : "";
        if (!msg && e && typeof e === "object" && "response" in e) {
          const data = (e as { response?: { data?: unknown } }).response?.data;
          msg = readCouponValidityMessage(data) ?? "";
        }
        setAppliedCoupon(null);
        lastCouponValidityKeyRef.current = "";
        showNotification(
          msg || "Could not validate coupon.",
          "error",
          COUPON_APPLY_NOTIFY_MS,
        );
      } finally {
        if (!cancelled && flightId === couponPlanRevalidateFlightRef.current) {
          setCouponApplyLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeStep,
    appliedCoupon,
    bookingDetails,
    costBreakdownLoading,
    costBreakdownRaw.planprice,
    onlyVehicleChangeNoPlan,
    promoCodeMetaByValue,
    selectedPlanId,
    showNotification,
    state.renterUserId,
  ]);

  const getEditBookingPaymentMethodCode = (): number | undefined => {
    const row = paymentmentCodes.find(
      (c) =>
        String(c.codedisplayname ?? "")
          .trim()
          .toLowerCase() === "cash",
    );
    if (!row) return undefined;
    const n = Number.parseInt(String(row.codevalue));
    return Number.isFinite(n) ? n : undefined;
  };

  /**
   * Resolves numeric `paymentmethod` for online/Razorpay from Code Common "PaymentMethod".
   * Master data rarely uses the exact string "online" — often "Online Payment", "Razorpay", "UPI", etc.
   */
  const getOnlinePaymentMethodCode = (): number | undefined => {
    const list = paymentmentCodes;
    if (!list?.length) return undefined;

    const norm = (s: unknown) =>
      String(s ?? "")
        .trim()
        .toLowerCase();
    const codeFromRow = (r: { codevalue?: string | number }) => {
      const n = Number.parseInt(String(r.codevalue), 10);
      return Number.isFinite(n) ? n : undefined;
    };

    // 1) Exact "online"
    let row = list.find((c) => norm(c.codedisplayname) === "online");
    if (row) return codeFromRow(row);

    // 2) Display name contains "online" (e.g. "Online Payment", "Pay Online")
    row = list.find((c) => norm(c.codedisplayname).includes("online"));
    if (row) return codeFromRow(row);

    // 3) Common gateway / non-cash labels used in master data
    const keywords = [
      "razorpay",
      "upi",
      "card",
      "netbanking",
      "digital",
      "gateway",
      "pg",
      "wallet",
    ];
    for (const kw of keywords) {
      row = list.find((c) => norm(c.codedisplayname).includes(kw));
      if (row) {
        const code = codeFromRow(row);
        if (code != null) return code;
      }
    }

    // 4) If only Cash + one other method exist, use the non-cash row for "online" in the UI
    const cashIdx = list.findIndex((c) => norm(c.codedisplayname) === "cash");
    if (list.length >= 2 && cashIdx >= 0) {
      const other = list.find((_, i) => i !== cashIdx);
      if (other) {
        const code = codeFromRow(other);
        if (code != null) return code;
      }
    }

    return undefined;
  };

  const buildUpdateBookingPayload = (): any => {
    const b = bookingDetails || {};
    const toNum = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const toStr = (v: unknown): string => (v == null ? "" : String(v));

    const bookingMasterId = toNum(b.bookingmasterid ?? bookingIdFromUrl);
    const hubId = toNum(selectedHubId);
    const vehicleId = toNum(selectedVehicleId);
    const vehicleModelId = toNum(selectedModelId);
    const planIdNum = onlyVehicleChangeNoPlan
      ? toNum(b.planid)
      : toNum(selectedPlanId);

    const selectedVehicle = hubVehicles.find(
      (v: any) => String(v.vehicleid ?? v.id) === String(selectedVehicleId),
    );
    const planRowKey = onlyVehicleChangeNoPlan ? b.planid : selectedPlanId;
    const selectedPlan = plansData.find(
      (p: any) => String(p.planid ?? p.id) === String(planRowKey),
    );
    const selectedHub = hubs.find(
      (h: any) => Number(h.hubmasterid) === Number(selectedHubId),
    );

    const planStartFromBooking = (): string => {
      const raw = b.planstartdate ?? b.planStartDate;
      if (raw == null || String(raw).trim() === "") return "";
      const d = dayjs(raw);
      if (!d.isValid()) return String(raw).trim();
      return toBookingPlanStartUtcIso(d.toISOString());
    };
    const planEndFromBooking = (): string => {
      const raw = b.planenddate ?? b.planEndDate;
      if (raw == null || String(raw).trim() === "") return "";
      const d = dayjs(raw);
      if (!d.isValid()) return String(raw).trim();
      return toBookingPlanEndUtcIso(d.toISOString());
    };

    const planStartIso = newPlanWindowForEdit?.planStartISO ?? "";
    const planEndIso = newPlanWindowForEdit?.planEndISO ?? "";
    const planStartDate = onlyVehicleChangeNoPlan
      ? planStartFromBooking()
      : planStartIso
        ? toBookingPlanStartUtcIso(planStartIso)
        : "";
    const planEndDate = onlyVehicleChangeNoPlan
      ? planEndFromBooking()
      : planEndIso
        ? toBookingPlanEndUtcIso(planEndIso)
        : "";
    const pickupStartTime = planStartDate;
    const pickupEndTime = planEndDate;

    const apiTotalNum = totalPayable
      ? Number.parseFloat(totalPayable.replaceAll(/[₹,\s]/g, "")) || 0
      : 0;
    const lateParsed =
      lateFeesAmount !== ""
        ? Number.parseFloat(String(lateFeesAmount).trim())
        : lateFeesBaseline;
    const lateCur =
      Number.isFinite(lateParsed) && lateParsed >= 0
        ? lateParsed
        : lateFeesBaseline;
    const discountNum =
      appliedCoupon?.discountValue != null
        ? Number(appliedCoupon.discountValue)
        : Number.NaN;
    const discountForMath =
      Number.isFinite(discountNum) && discountNum > 0 ? discountNum : 0;
    const discountedAmountPayload: number | null =
      Number.isFinite(discountNum) && discountNum > 0 ? discountNum : null;
    const bookingAmount = Math.max(
      0,
      apiTotalNum + (lateCur - lateFeesBaseline) - discountForMath,
    );
    const cashCharge =
      paymentMethod === "cash" && cashPaymentCharge !== ""
        ? Number(cashPaymentCharge) || 0
        : 0;
    const createdBy = toNum(b.createdby ?? b.tripStart?.createdby);
    const modifiedBy = createdBy;
    const managerId = toNum(user?.userid);

    const oldTripMasterId = toNum(b.tripStart?.tripmasterid);

    const couponid =
      appliedCoupon?.couponid != null ? toNum(appliedCoupon.couponid) : 0;
    const couponname = appliedCoupon?.name?.trim?.()
      ? String(appliedCoupon.name).trim()
      : "";
    const isReferralCoupon = appliedCoupon?.isReferralCoupon === true;

    return {
      bookingmasterid: bookingMasterId,
      hubid: hubId,
      vehicleid: vehicleId,
      evtypeid: toNum(selectedVehicle?.speedtype),
      vehicletypeid: toNum(selectedVehicle?.vehicletype),
      createdby: createdBy,
      modifiedby: modifiedBy,
      pickupstarttime: pickupStartTime,
      pickupendtime: pickupEndTime,
      name: toStr(customerName),
      planname: toStr(selectedPlan?.planname ?? b.planname ?? ""),
      vehiclename: toStr(selectedVehicle?.vehiclename ?? b.vehiclename),
      phonenumber: toStr(phoneNumber ?? b.phonenumber),
      hubname: toStr(selectedHub?.hubname ?? b.hubname),
      telematicsdeviceid: toStr(
        selectedVehicle?.telematicsdeviceid ?? b.telematicsdeviceid,
      ),
      vehiclemodelid: vehicleModelId,
      vehiclenum: toStr(selectedVehicle?.vehiclenum ?? b.vehiclenum),
      registrationnumber: toStr(
        selectedVehicle?.registrationnumber ?? b.registrationnumber,
      ),
      managerid: managerId,
      refundamount: toNum(b.refundamount),
      bookingPlanUpdateModel: {
        bookingplanid: toNum(b.bookingplanid),
        bookingmasterid: bookingMasterId,
        planid: planIdNum,
        ratetemplatemasterid: toNum(
          selectedPlan?.templatemasterid ??
            selectedPlan?.ratetemplatemasterid ??
            selectedPlan?.rateTemplateMasterId ??
            b.ratetemplatemasterid ??
            b.rateTemplateMasterId,
        ),
        planstartdate: planStartDate,
        planenddate: planEndDate,
        bookingamount: bookingAmount,
        remarks: toStr(remarks),
        cashpaymentchargeamount: cashCharge,
        planprice: toNum(costBreakdownRaw.planprice),
        processing_fees: toNum(costBreakdownRaw.processing_fees),
        refundable_amount: toNum(costBreakdownRaw.refundable_amount),
        couponid,
        couponname,
        discountedamount: discountedAmountPayload,
        isReferralCoupon,
        latefees: lateCur,
      },
      feesModificationModel: {
        fee_modification_id: 0,
        bookingmasterid: bookingMasterId,
        planid: planIdNum,
        additional_amount: toNum(additionalFees),
        managerid: managerId,
      },
      oldvehiclechecklistupdate:
        tripStatusFromApi === 5
          ? {
              tripmasterid: oldTripMasterId,
              vehiclephotos:
                vehiclePhotos.length > 0 ? vehiclePhotos.join(",") : "",
              batteryphotos:
                batteryPhotos.length > 0 ? batteryPhotos.join(",") : "",
              chargerphotos:
                chargerPhotos.length > 0 ? chargerPhotos.join(",") : "",
              e_signature: toStr(b.tripStart?.e_signature ?? b.e_signature),
              terms_condition_accepted: true,
              tripstatus: toNum(b.tripstatus),
              triptype: toNum(b.tripStart?.triptype),
              userid: createdBy,
              vehiclechecklistnote: "",
              modifiedby: modifiedBy,
              bookingmasterid: bookingMasterId,
              receiveragentname: toStr(b.tripStart?.receiveragentname),
              isbookingupdated: true,
              vehicleconditionchecklistmappings: (() => {
                const apiArr = b.tripStart?.vehicleconditionchecklistmappings;
                const fromApi: Record<string, boolean> = {};
                const allIds = new Set<string>();
                if (Array.isArray(apiArr)) {
                  (apiArr as any[]).forEach((m: any) => {
                    const id = m?.vehicleconditionchecklistid;
                    if (id == null) return;
                    const key = String(id);
                    fromApi[key] = m?.vehicleconditioncheckedstatus === true;
                    allIds.add(key);
                  });
                } else if (typeof apiArr === "object" && apiArr != null) {
                  Object.entries(apiArr as Record<string, unknown>).forEach(
                    ([k, v]) => {
                      fromApi[k] = v === true;
                      allIds.add(k);
                    },
                  );
                }
                Object.keys(vehicleConditionChecklistMappings).forEach((k) =>
                  allIds.add(k),
                );
                const out: Record<string, boolean> = {};
                allIds.forEach((id) => {
                  if (Object.hasOwn(vehicleConditionChecklistMappings, id)) {
                    out[id] = vehicleConditionChecklistMappings[id] === true;
                  } else {
                    out[id] = fromApi[id] === true;
                  }
                });
                return out;
              })(),
            }
          : {
              tripmasterid: 0,
              vehiclephotos: "",
              batteryphotos: "",
              chargerphotos: "",
              e_signature: toStr(b.tripStart?.e_signature ?? b.e_signature),
              terms_condition_accepted: true,
              tripstatus: toNum(b.tripstatus),
              triptype: 0,
              userid: createdBy,
              vehiclechecklistnote: "",
              modifiedby: modifiedBy,
              bookingmasterid: bookingMasterId,
              receiveragentname: "",
              isbookingupdated: false,
              vehicleconditionchecklistmappings: {},
            },
      newvehiclechecklistupdate:
        selectedVehicleId != null &&
        b.vehicleid != null &&
        Number(selectedVehicleId) !== Number(b.vehicleid)
          ? {
              tripmasterid: 0,
              vehiclephotos: newVehiclePhotos.join(","),
              batteryphotos: newVehicleBatteryPhotos.join(","),
              chargerphotos: newVehicleChargerPhotos.join(","),
              e_signature: toStr(b.tripStart?.e_signature ?? b.e_signature),
              terms_condition_accepted: true,
              tripstatus: toNum(b.tripstatus),
              triptype: 1,
              userid: createdBy,
              vehiclechecklistnote: "",
              modifiedby: modifiedBy,
              bookingmasterid: bookingMasterId,
              receiveragentname: "",
              isbookingupdated: true,
              vehicleconditionchecklistmappings: Object.fromEntries(
                Object.entries(newVehicleChecklistMappings).map(([k, v]) => [
                  k,
                  v === true,
                ]),
              ),
            }
          : {
              tripmasterid: 0,
              vehiclephotos: "",
              batteryphotos: "",
              chargerphotos: "",
              e_signature: toStr(b.tripStart?.e_signature ?? b.e_signature),
              terms_condition_accepted: true,
              tripstatus: toNum(b.tripstatus),
              triptype: 0,
              userid: createdBy,
              vehiclechecklistnote: "",
              modifiedby: modifiedBy,
              bookingmasterid: bookingMasterId,
              receiveragentname: "",
              isbookingupdated: false,
              vehicleconditionchecklistmappings: {},
            },
    };
  };

  const validateSavePrerequisites = (): boolean => {
    if (!bookingIdFromUrl) {
      setError("Booking ID is required to save.");
      return false;
    }
    const reasonTrimmedSave = typeof reason === "string" ? reason.trim() : "";
    if (reasonTrimmedSave === "") {
      setError(REASON_REQUIRED_MESSAGE);
      return false;
    }
    if (
      updateVehicleRemarksOtherCodeValue !== "" &&
      reasonTrimmedSave === updateVehicleRemarksOtherCodeValue &&
      remarks.trim() === ""
    ) {
      setError(OTHER_REMARKS_REQUIRED_MESSAGE);
      return false;
    }
    if (!vehicleStepValid) {
      setShowVehicleUploadValidation(true);
      let saveMessage: string;
      const hasUpdateChecklistSelection = Object.values(
        vehicleConditionChecklistMappings,
      ).includes(true);
      const hasNewChecklistSelection = Object.values(
        newVehicleChecklistMappings,
      ).includes(true);
      if (kycSelectionInvalid) {
        saveMessage =
          kycVehicleStepBlockMessage ?? KYC_INCOMPLETE_VEHICLE_MESSAGE;
      } else if (hasUpdateSection && !hasUpdateChecklistSelection) {
        saveMessage =
          "Select at least one item in Update existing vehicle checklist before saving.";
      } else if (hasUpdateSection && vehiclePhotos.length === 0) {
        saveMessage =
          "Vehicle images are required. Please upload at least one vehicle image before saving.";
      } else if (
        hasUpdateSection &&
        originalVehicleType === 1 &&
        (batteryPhotos.length === 0 || chargerPhotos.length === 0)
      ) {
        saveMessage =
          "Battery and charger images are required for this vehicle. Please upload at least one of each before saving.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        !hasNewChecklistSelection
      ) {
        saveMessage =
          "Select at least one item in New vehicle checklist before saving.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        newVehiclePhotos.length === 0
      ) {
        saveMessage =
          "Vehicle images are required for the new vehicle. Please upload at least one vehicle image before saving.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        selectedVehicleType === 1 &&
        (newVehicleBatteryPhotos.length === 0 ||
          newVehicleChargerPhotos.length === 0)
      ) {
        saveMessage =
          "Battery and charger images are required for the new vehicle. Please upload at least one of each before saving.";
      } else {
        saveMessage =
          "Please upload all required vehicle images before saving.";
      }
      setError(saveMessage);
      return false;
    }
    return true;
  };

  const handleSaveClick = () => {
    if (!validateSavePrerequisites()) return;
    setSaveConfirmOpen(true);
  };

  const executeSave = async () => {
    if (!validateSavePrerequisites()) {
      setSaveLoading(false);
      return;
    }
    setError(null);
    try {
      const payload = buildUpdateBookingPayload();
      const bdForPayment = bookingDetails as Record<string, unknown> | null;
      const bookingPlanIdForPayment = toBookingPlanIdNum(
        bdForPayment?.bookingplanid ?? bdForPayment?.bookingPlanId,
      );

      const amountForPayment =
        Number.parseFloat(
          String(displayTotalPayable).replaceAll(/[₹,\s]/g, ""),
        ) || 0;

      const effectivePlanIdForLabel = onlyVehicleChangeNoPlan
        ? bookingDetails?.planid
        : selectedPlanId;
      const selectedPlanRow = plansData.find(
        (p: any) =>
          String(p.planid ?? p.id) === String(effectivePlanIdForLabel),
      );
      const planLabel =
        `${selectedPlanRow?.planname ?? planOptions.find((p) => String(p.id) === String(effectivePlanIdForLabel))?.name ?? bookingDetails?.planname ?? ""}`.trim();
      const modelLabel =
        `${selectedVehicleModelDisplayName ?? ""}`.trim() || "Vehicle model";
      const summeryofpayments = `Renewal payment for ${modelLabel || "-"} - ${planLabel || "-"}`;

      const downgradeRefundBookingMasterId = Number(
        bookingDetails?.bookingmasterid ?? bookingIdFromUrl ?? 0,
      );
      const downgradeRefundBookingPlanId = Number(
        bookingDetails?.bookingplanid ?? 0,
      );

      const runDowngradeRefundsIfNeeded = async () => {
        if (!shouldIssueDowngradeRefunds(costBreakdownRaw)) return;
        try {
          await callAddRefundForDowngradeIfNeeded(
            downgradeRefundBookingMasterId,
            downgradeRefundBookingPlanId,
            costBreakdownRaw,
          );
        } catch (refErr: unknown) {
          console.warn("addRefund (plan downgrade)", refErr);
          const m =
            refErr instanceof Error && refErr.message.trim() !== ""
              ? refErr.message
              : "Could not register downgrade refund.";
          showNotification(m, "error");
        }
      };

      if (amountForPayment > 0 && paymentMethod === "online") {
        const discountedamountForSave =
          appliedCoupon?.discountValue != null &&
          Number.isFinite(Number(appliedCoupon.discountValue)) &&
          Number(appliedCoupon.discountValue) > 0
            ? Number(appliedCoupon.discountValue)
            : 0;

        const orderReceipt = `${bookingIdFromUrl}`;
        const amountPaise = Math.round(amountForPayment * 100);

        let orderResForDetails: unknown;
        let orderId: string;
        try {
          orderResForDetails = await apiService.createRazorpayOrder({
            amount: amountPaise,
            currency: "INR",
            receipt: orderReceipt,
            notes: {
              bookingId: String(bookingIdFromUrl),
              planName: planLabel,
              modelName: modelLabel,
              customer_name: paymentPayerName.trim() || "Customer",
              phone_number: String(
                paymentPayerMobile ?? phoneNumber ?? "",
              ).replace(/\D/g, ""),
            },
            extraobject: [{ type: "booking" }],
          });
          const extracted = extractRazorpayOrderId(orderResForDetails);
          if (!extracted) {
            throw new Error(
              "Failed to create Razorpay order. Order ID not received.",
            );
          }
          orderId = extracted;
        } catch (orderErr: any) {
          const msg =
            orderErr?.message ||
            orderErr?.response?.data?.message ||
            "Failed to create payment order.";
          setError(msg);
          setSaveLoading(false);
          return;
        }

        const onlinePaymentMethodCode = getOnlinePaymentMethodCode();
        if (onlinePaymentMethodCode == null) {
          setError(
            'Online payment method could not be resolved from master data. Ensure Code Common category "PaymentMethod" includes a non-cash method (e.g. Online, Online Payment, Razorpay, or UPI) with a valid code value.',
          );
          setSaveLoading(false);
          return;
        }

        const customerNameForPayment =
          paymentPayerName.trim() || customerName.trim() || "Customer";
        const customerContact = String(
          paymentPayerMobile ?? phoneNumber ?? "",
        ).replace(/\D/g, "");
        const customerEmail = "noreply@motovolt.in";
        const referenceId = `${orderId}`;
        const bd = bookingDetails as Record<string, unknown> | null | undefined;
        const renterUserIdForNotes = String(
          bd?.userid ?? bd?.userId ?? "",
        ).trim();

        const paymentLinkBody = {
          amount: amountPaise,
          currency: "INR",
          accept_partial: false,
          description: summeryofpayments,
          reference_id: referenceId,
          customer: {
            name: customerNameForPayment,
            email: customerEmail,
            contact: customerContact,
          },
          notify: { sms: true, email: true },
          reminder_enable: true,
          notes: {
            env: process.env.REACT_APP_ENV || "UAT",
            module: "EditBooking",
            razorpayOrderId: orderId,
            orderReceipt,
            bookingId: String(bookingIdFromUrl ?? ""),
            customerUserId: renterUserIdForNotes,
          },
        };

        const qrCodeBody = {
          type: "upi_qr",
          name: customerNameForPayment,
          usage: "single_use",
          fixed_amount: true,
          payment_amount: amountPaise,
          description: summeryofpayments,
          orderid: orderId,
          notes: {
            bookingId: String(bookingIdFromUrl ?? ""),
            customerUserId: renterUserIdForNotes,
          },
        };

        setOnlineCollectionPaymentLink("");
        setOnlineCollectionQrSrc("");
        setOnlineCollectionVerifyEntityId(null);
        setOnlineCollectionOrderId(orderId);
        setOnlineCollectionError(null);
        setOnlineCollectionUpdateDone(false);
        setOnlineCollectionDialogOpen(true);
        setOnlineCollectionLoading(true);
        setSaveLoading(false);

        try {
          let collectionRes: unknown;
          const onlinePayerMobile =
            `${paymentPayerMobile ?? ""}`.trim() ||
            `${phoneNumber ?? ""}`.trim() ||
            customerContact;

          if (postSavePaymentMode === "payment_link") {
            collectionRes = await apiService.createPaymentLink(paymentLinkBody);
            const link =
              extractPaymentLinkFromCollectionResponse(collectionRes);
            if (!link) {
              throw new Error("Payment link was not returned by the server.");
            }

            const updateRes = await apiService.updateBookingPayload(payload);
            const updateWrap = updateRes as {
              isSuccess?: boolean;
              message?: string;
            };
            if (
              updateWrap &&
              typeof updateWrap === "object" &&
              updateWrap.isSuccess === false
            ) {
              throw new Error(
                typeof updateWrap.message === "string" &&
                  updateWrap.message.trim() !== ""
                  ? updateWrap.message
                  : "Update booking failed.",
              );
            }
            setOnlineCollectionUpdateDone(true);
            const successMsg = extractUpdateBookingSuccessMessage(updateRes);
            if (successMsg) showNotification(successMsg, "success");

            await runDowngradeRefundsIfNeeded();

            const entityIdFromApi =
              extractPaymentIdFromCollectionResponse(collectionRes, "") || null;
            setOnlineCollectionVerifyEntityId(entityIdFromApi);
            const transactionRef =
              entityIdFromApi ??
              extractPaymentIdFromCollectionResponse(collectionRes, orderId);
            try {
              await apiService.savePaymentDetails({
                paymentid: 0,
                hubmasterid: selectedHubId != null ? Number(selectedHubId) : 0,
                bookingid: `${bookingIdFromUrl ?? ""}`.trim(),
                bookingplanid: resolveEditBookingPlanIdForPayment(
                  updateRes,
                  bdForPayment,
                ),
                username: customerNameForPayment,
                mobileno: onlinePayerMobile,
                hubname: `${selectedHubDisplayName ?? ""}`.trim(),
                amount: amountForPayment,
                paymentmethod: onlinePaymentMethodCode,
                summeryofpayments,
                transactionid: transactionRef,
                enteredon: dayjs().toISOString(),
                discountedamount: discountedamountForSave,
                orderid: orderId,
              });
            } catch (dErr: unknown) {
              console.warn("SavePaymentDetails failed", dErr);
            }

            setOnlineCollectionPaymentLink(link);
          } else {
            collectionRes = await apiService.createQrCode(qrCodeBody);
            const qr = extractQrImageSrcFromCollectionResponse(collectionRes);
            if (!qr) {
              throw new Error("QR image was not returned by the server.");
            }

            const updateRes = await apiService.updateBookingPayload(payload);
            const updateWrap = updateRes as {
              isSuccess?: boolean;
              message?: string;
            };
            if (
              updateWrap &&
              typeof updateWrap === "object" &&
              updateWrap.isSuccess === false
            ) {
              throw new Error(
                typeof updateWrap.message === "string" &&
                  updateWrap.message.trim() !== ""
                  ? updateWrap.message
                  : "Update booking failed.",
              );
            }
            setOnlineCollectionUpdateDone(true);
            const successMsg = extractUpdateBookingSuccessMessage(updateRes);
            if (successMsg) showNotification(successMsg, "success");

            await runDowngradeRefundsIfNeeded();

            const entityIdFromApi =
              extractPaymentIdFromCollectionResponse(collectionRes, "") || null;
            setOnlineCollectionVerifyEntityId(entityIdFromApi);
            const transactionRef =
              entityIdFromApi ??
              extractPaymentIdFromCollectionResponse(collectionRes, orderId);
            try {
              await apiService.savePaymentDetails({
                paymentid: 0,
                hubmasterid: selectedHubId != null ? Number(selectedHubId) : 0,
                bookingid: `${bookingIdFromUrl ?? ""}`.trim(),
                bookingplanid: resolveEditBookingPlanIdForPayment(
                  updateRes,
                  bdForPayment,
                ),
                username: customerNameForPayment,
                mobileno: onlinePayerMobile,
                hubname: `${selectedHubDisplayName ?? ""}`.trim(),
                amount: amountForPayment,
                paymentmethod: onlinePaymentMethodCode,
                summeryofpayments,
                transactionid: transactionRef,
                enteredon: dayjs().toISOString(),
                discountedamount: discountedamountForSave,
                orderid: orderId,
              });
            } catch (dErr: unknown) {
              console.warn("SavePaymentDetails failed", dErr);
            }

            setOnlineCollectionQrSrc(qr);
          }
        } catch (err: any) {
          const msg =
            err?.message ||
            err?.response?.data?.message ||
            "Failed to update booking or create payment collection.";
          setOnlineCollectionError(msg);
        } finally {
          setOnlineCollectionLoading(false);
        }
        return;
      } else if (amountForPayment > 0 && paymentMethod === "cash") {
        // Cash payment flow: Save payment → Update booking
        const cashPaymentMethodCode = getEditBookingPaymentMethodCode();
        if (cashPaymentMethodCode == null) {
          setError(
            "Cash payment method could not be resolved from master data. Ensure PaymentMethod codes are loaded and Cash is configured.",
          );
          setSaveLoading(false);
          return;
        }

        const paymentBody = {
          paymentid: 0,
          hubmasterid: selectedHubId != null ? Number(selectedHubId) : 0,
          bookingid: `${bookingIdFromUrl ?? ""}`.trim(),
          username: paymentPayerName.trim() || "Customer",
          mobileno: paymentPayerMobile.trim(),
          hubname: `${selectedHubDisplayName ?? ""}`.trim(),
          amount: amountForPayment,
          paymentmethod: cashPaymentMethodCode,
          summeryofpayments,
          bookingplanid: bookingPlanIdForPayment,
          transactionid: "cash_" + Date.now().toString(),
          enteredon: dayjs().toISOString(),
          orderid: "",
        };

        await apiService.savePaymentDetails(paymentBody);
      }

      // Update booking (for cash or zero amount; online uses link/QR dialog)
      await apiService.updateBookingPayload(payload);

      await runDowngradeRefundsIfNeeded();

      showNotification("Booking updated successfully.", "success");
      navigate("/booking-management", { state: { shouldRefetch: true } });
    } catch (e: any) {
      const msg =
        e?.message ||
        e?.response?.data?.message ||
        "Failed to save payment or update booking.";
      setError(msg);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleConfirmSave = () => {
    setSaveConfirmOpen(false);
    setSaveLoading(true);
    void executeSave();
  };

  const handleVerifyQrPayment = useCallback(async () => {
    const oid = onlineCollectionOrderId.trim();
    if (!oid) {
      showNotification("Order ID is missing.", "error");
      return;
    }
    setVerifyPaymentLoading(true);
    try {
      const res = (await apiService.verifyPaymentByOrderId(
        oid,
        postSavePaymentMode === "payment_link" ? "payment_link" : "qr_code",
        onlineCollectionVerifyEntityId,
      )) as Record<string, unknown>;
      const failed =
        res &&
        typeof res === "object" &&
        (res.isSuccess === false || res.success === false);
      const msg =
        (typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : null) ??
        (typeof res?.Message === "string" && res.Message.trim() !== ""
          ? res.Message.trim()
          : null) ??
        (failed ? "Verification failed." : "Payment verified successfully.");
      if (failed) {
        showNotification(msg, "error");
        return;
      }
      const done = onlineCollectionUpdateDone;
      showNotification(msg, "success");
      setOnlineCollectionDialogOpen(false);
      setOnlineCollectionError(null);
      setOnlineCollectionPaymentLink("");
      setOnlineCollectionQrSrc("");
      setOnlineCollectionOrderId("");
      setOnlineCollectionVerifyEntityId(null);
      setOnlineCollectionUpdateDone(false);
      if (done) {
        navigate("/booking-management", { state: { shouldRefetch: true } });
      }
    } catch (e: unknown) {
      const msg =
        e instanceof Error && e.message.trim() !== ""
          ? e.message
          : "Verification request failed.";
      showNotification(msg, "error");
    } finally {
      setVerifyPaymentLoading(false);
    }
  }, [
    onlineCollectionOrderId,
    onlineCollectionVerifyEntityId,
    onlineCollectionUpdateDone,
    postSavePaymentMode,
    showNotification,
    navigate,
  ]);

  if (loadingDetails && bookingIdFromUrl) {
    return <CustomLoading message="Loading booking details..." />;
  }

  return (
    <Container
      fluid
      className="px-0"
      style={{ maxWidth: "100%", overflowX: "hidden" }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          px: { xs: 1.5, sm: 3, md: 4 },
          pb: { xs: 3, sm: 4 },
        }}
      >
        {saveLoading && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 2,
              bgcolor: "rgba(255, 255, 255, 0.14)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CustomLoading message="Saving booking..." />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e0e0e0",
            bgcolor: "#fff",
            borderRadius: 2,
            width: "100%",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              borderBottom: "2px solid",
              borderColor: THEME_PRIMARY,
              pb: 1,
              mb: 2,
              color: THEME_PRIMARY,
              fontSize: { xs: 16, sm: 18 },
            }}
          >
            Customer Information
          </Typography>
          <Row className="g-3">
            <Col xs={12} sm={6} md={3}>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mb: 0.25 }}
              >
                Booking ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bookingIdFromUrl}
              </Typography>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mb: 0.25 }}
              >
                Customer Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {customerName}
              </Typography>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mb: 0.25 }}
              >
                Phone Number
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {phoneNumber}
              </Typography>
            </Col>
          </Row>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: { xs: 2, sm: 3 },
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e0e0e0",
            bgcolor: "#fff",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 2.5, sm: 4, md: 5, lg: 10 },
              justifyContent: "flex-start",
              alignItems: "flex-end",
              mb: { xs: 3, sm: 4 },
            }}
          >
            {steps.map((label, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              let textColor = "grey.500";
              let underlineColor = "grey.400";
              if (isCompleted) {
                textColor = "success.main";
                underlineColor = "success.main";
              } else if (isActive) {
                textColor = THEME_PRIMARY;
                underlineColor = THEME_PRIMARY;
              }
              return (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    flex: { xs: "1 1 40%", sm: "0 0 auto" },
                    minWidth: 0,
                    borderBottom: "2px solid",
                    borderColor: underlineColor,
                    pb: 1.25,
                    "&:first-of-type": { ml: 0 },
                    "&:last-of-type": { mr: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 0.75 },
                      flexWrap: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {isCompleted ? (
                      <CheckCircleIcon
                        sx={{
                          color: "success.main",
                          fontSize: { xs: 20, sm: 24, md: 26 },
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: textColor,
                        fontSize: {
                          xs: "0.9375rem",
                          sm: "1rem",
                          md: "1.0625rem",
                        },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {isCompleted ? label : `${index + 1}. ${label}`}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {activeStep === 0 && (
            <>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, sm: 2 },
                  color: "text.primary",
                  fontSize: { xs: 18, sm: 20 },
                }}
              >
                Please Select Hub
              </Typography>
              {hubsLoading ? (
                <CustomLoading message="Loading hubs..." />
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    },
                    gap: { xs: 1.5, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                    alignItems: "stretch",
                  }}
                >
                  {hubCards.map((hub) => {
                    const isSelected = selectedHubId === hub.hubmasterid;
                    return (
                      <Card
                        key={hub.hubmasterid}
                        onClick={() => handleHubSelect(hub.hubmasterid)}
                        sx={{
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: isSelected
                            ? THEME_PRIMARY_LIGHT
                            : "rgba(22, 67, 135, 0.25)",
                          borderRadius: "12px",
                          bgcolor: isSelected ? "#E8E8FF" : "#fff",
                          boxShadow: "none",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: { xs: 180, sm: 220 },
                          "&:hover": {
                            borderColor: THEME_PRIMARY_LIGHT,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 0,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            "&:last-child": { pb: 0 },
                          }}
                        >
                          <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                px: { xs: 1.5, sm: 2 },
                                pt: { xs: 1.5, sm: 2 },
                                pb: 1,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: THEME_PRIMARY,
                                  fontSize: { xs: "1rem", sm: "1.1rem" },
                                  mb: 2,
                                  lineHeight: 1.4,
                                }}
                              >
                                {hub.hubname}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                  mb: 0.5,
                                }}
                              >
                                <LocationOnIcon
                                  sx={{
                                    fontSize: 20,
                                    color: THEME_PRIMARY,
                                    mt: 0.25,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666666",
                                    fontSize: {
                                      xs: "0.8125rem",
                                      sm: "0.875rem",
                                    },
                                    lineHeight: 1.5,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {hub.address}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <DirectionsWalkIcon
                                  sx={{
                                    fontSize: 20,
                                    color: THEME_PRIMARY,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666666",
                                    fontSize: {
                                      xs: "0.8125rem",
                                      sm: "0.875rem",
                                    },
                                  }}
                                >
                                  {hub.operationhours}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                width: { xs: 88, sm: 110, md: 120 },
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 1.5,
                                alignSelf: "stretch",
                              }}
                            >
                              <Box
                                component="img"
                                src={hub.imageUrl || "/scooter-placeholder.png"}
                                alt={hub.hubname}
                                onError={(
                                  e: React.SyntheticEvent<HTMLImageElement>,
                                ) => {
                                  e.currentTarget.src =
                                    "/scooter-placeholder.png";
                                }}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  minHeight: 80,
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  maxWidth: { xs: 72, sm: 88, md: 104 },
                                  maxHeight: 120,
                                }}
                              />
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              borderTop: "1px solid",
                              borderTopColor: isSelected
                                ? THEME_PRIMARY
                                : "#DDDDDD",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                              px: { xs: 1.5, sm: 2 },
                              py: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexWrap: "wrap",
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#666666",
                                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                }}
                              >
                                {hub.available}
                              </Typography>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: "#666666",
                                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                }}
                              >
                                {" "}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigate(hub);
                              }}
                              startIcon={
                                <NavigationIcon
                                  sx={{ fontSize: 20, color: THEME_PRIMARY }}
                                />
                              }
                              sx={{
                                bgcolor: "#FFC107",
                                color: THEME_PRIMARY,
                                fontWeight: 500,
                                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                minWidth: 108,
                                flexShrink: 0,
                                borderRadius: "8px",
                                px: 1.5,
                                py: 0.75,
                                "&:hover": { bgcolor: "#FFB300" },
                              }}
                            >
                              Navigate
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  boxShadow: "none",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ mb: 2, maxWidth: 250 }}>
                  <SearchableSelectInput
                    field="reason"
                    formData={{ reason }}
                    onChange={(field, value) => {
                      if (field !== "reason") return;
                      const v = String(value ?? "").trim();
                      setReason(v);
                      setError(null);
                      const selectedCode = updateVehicleRemarksCodes.find(
                        (c) => String(c.codevalue ?? "").trim() === v,
                      );
                      if (
                        updateVehicleRemarksOtherCodeValue !== "" &&
                        v === updateVehicleRemarksOtherCodeValue
                      ) {
                        setRemarks("");
                      } else {
                        setRemarks(selectedCode?.codedisplayname ?? "");
                      }
                    }}
                    options={updateVehicleRemarksSelectOptions}
                    label="Select Reason"
                    required
                    loading={updateVehicleRemarksCodesLoading}
                    error={
                      error === REASON_REQUIRED_MESSAGE
                        ? REASON_REQUIRED_MESSAGE
                        : undefined
                    }
                  />
                </Box>
                {updateVehicleRemarksOtherCodeValue !== "" &&
                  reason === updateVehicleRemarksOtherCodeValue && (
                    <TextField
                      label={
                        <Box component="span">
                          Add Remarks
                          <Box
                            component="span"
                            sx={{ color: "#d32f2f", ml: 0.25 }}
                          >
                            *
                          </Box>
                        </Box>
                      }
                      multiline
                      rows={4}
                      value={remarks}
                      onChange={(e) => {
                        setRemarks(e.target.value);
                        setError(null);
                      }}
                      error={error === OTHER_REMARKS_REQUIRED_MESSAGE}
                      helperText={
                        error === OTHER_REMARKS_REQUIRED_MESSAGE
                          ? OTHER_REMARKS_REQUIRED_MESSAGE
                          : undefined
                      }
                      fullWidth
                      size="small"
                      placeholder="Enter remarks..."
                    />
                  )}
              </Paper>
            </>
          )}

          {activeStep === 1 && (
            <SelectVehicleModelStep
              options={vehicleModelOptions}
              selectedId={selectedModelId}
              onSelect={setSelectedModelId}
              loading={vehiclesLoading}
              selectedHubName={selectedHubDisplayName}
              kycBlocksHighSpeedModels={
                customerKycState.checked &&
                (!customerKycState.verified ||
                  !customerKycState.hasDlOcrFullname)
              }
              kycCheckLoading={customerKycState.loading}
              kycHighSpeedMessage={KYC_HIGH_SPEED_DL_MESSAGE}
              currentBookingVehicle={
                bookingVehiclePlanSummary
                  ? {
                      vehicleName: bookingVehiclePlanSummary.vehicleName,
                      vehicleNum: bookingVehiclePlanSummary.vehicleNum,
                      registrationNumber:
                        bookingVehiclePlanSummary.registrationNumber,
                      imageUrl: bookingVehiclePlanSummary.imageUrl,
                      speed: bookingVehiclePlanSummary.speed,
                      range: bookingVehiclePlanSummary.range,
                      hubName: bookingVehiclePlanSummary.hubName,
                      modelName: bookingVehiclePlanSummary.modelName,
                      currentPlanName: bookingVehiclePlanSummary.planName,
                      bookingAmount: bookingVehiclePlanSummary.bookingAmount,
                      planStartDate: bookingVehiclePlanSummary.planStartDate,
                      planEndDate: bookingVehiclePlanSummary.planEndDate,
                    }
                  : null
              }
            />
          )}

          {activeStep === 2 && (
            <SelectVehicleStep
              vehicles={vehicleOptions}
              selectedId={selectedVehicleId}
              onSelect={handleVehicleStepSelect}
              loading={vehiclesLoading}
              vehicleListSearch={
                selectedHubId != null &&
                selectedModelId != null &&
                selectedModelId !== ""
                  ? {
                      hubMasterId: Number(selectedHubId),
                      vehicleModelId: selectedModelId,
                      getSpeedTypeDisplayName,
                    }
                  : undefined
              }
              selectedHubName={selectedHubDisplayName}
              selectedVehicleModelName={selectedVehicleModelDisplayName}
              currentBookingVehicle={
                bookingVehiclePlanSummary
                  ? {
                      vehicleName: bookingVehiclePlanSummary.vehicleName,
                      vehicleNum: bookingVehiclePlanSummary.vehicleNum,
                      registrationNumber:
                        bookingVehiclePlanSummary.registrationNumber,
                      imageUrl: bookingVehiclePlanSummary.imageUrl,
                      speed: bookingVehiclePlanSummary.speed,
                      range: bookingVehiclePlanSummary.range,
                      hubName: bookingVehiclePlanSummary.hubName,
                      modelName: bookingVehiclePlanSummary.modelName,
                      currentPlanName: bookingVehiclePlanSummary.planName,
                      bookingAmount: bookingVehiclePlanSummary.bookingAmount,
                      planStartDate: bookingVehiclePlanSummary.planStartDate,
                      planEndDate: bookingVehiclePlanSummary.planEndDate,
                    }
                  : null
              }
              showVehicleUploadValidation={showVehicleUploadValidation}
              vehiclePhotos={vehiclePhotos}
              batteryPhotos={batteryPhotos}
              chargerPhotos={chargerPhotos}
              onVehiclePhotosChange={(added) =>
                setVehiclePhotos((prev) => [...prev, ...added])
              }
              onBatteryPhotosChange={(added) =>
                setBatteryPhotos((prev) => [...prev, ...added])
              }
              onChargerPhotosChange={(added) =>
                setChargerPhotos((prev) => [...prev, ...added])
              }
              onVehiclePhotoDelete={(index) =>
                setVehiclePhotos((prev) => prev.filter((_, i) => i !== index))
              }
              onBatteryPhotoDelete={(index) =>
                setBatteryPhotos((prev) => prev.filter((_, i) => i !== index))
              }
              onChargerPhotoDelete={(index) =>
                setChargerPhotos((prev) => prev.filter((_, i) => i !== index))
              }
              checklistMappings={vehicleConditionChecklistMappings}
              onChecklistChange={setVehicleConditionChecklistMappings}
              tripStatus={tripStatusFromApi}
              tripStart={bookingDetails?.tripStart ?? null}
              originalVehicleId={
                bookingDetails?.vehicleid != null
                  ? Number(bookingDetails.vehicleid)
                  : null
              }
              isEditingOldChecklist={isEditingOldChecklist}
              onEditingOldChecklistChange={setIsEditingOldChecklist}
              newVehiclePhotos={newVehiclePhotos}
              newVehicleBatteryPhotos={newVehicleBatteryPhotos}
              newVehicleChargerPhotos={newVehicleChargerPhotos}
              newVehicleChecklistMappings={newVehicleChecklistMappings}
              onNewVehiclePhotosChange={(added) =>
                setNewVehiclePhotos((prev) => [...prev, ...added].slice(0, 5))
              }
              onNewVehicleBatteryPhotosChange={(added) =>
                setNewVehicleBatteryPhotos((prev) =>
                  [...prev, ...added].slice(0, 3),
                )
              }
              onNewVehicleChargerPhotosChange={(added) =>
                setNewVehicleChargerPhotos((prev) =>
                  [...prev, ...added].slice(0, 3),
                )
              }
              onNewVehiclePhotoDelete={(index) =>
                setNewVehiclePhotos((prev) =>
                  prev.filter((_, i) => i !== index),
                )
              }
              onNewVehicleBatteryPhotoDelete={(index) =>
                setNewVehicleBatteryPhotos((prev) =>
                  prev.filter((_, i) => i !== index),
                )
              }
              onNewVehicleChargerPhotoDelete={(index) =>
                setNewVehicleChargerPhotos((prev) =>
                  prev.filter((_, i) => i !== index),
                )
              }
              onNewVehicleChecklistChange={setNewVehicleChecklistMappings}
              originalVehicleType={originalVehicleType}
              selectedVehicleType={selectedVehicleType}
              kycBlocksOnePersonVehicles={kycBlocksOnePersonVehicles}
              kycBlocksHighSpeedVehicles={kycBlocksHighSpeedVehicles}
              kycCheckLoading={customerKycState.loading}
              kycIncompleteMessage={KYC_INCOMPLETE_VEHICLE_MESSAGE}
              kycHighSpeedMessage={KYC_HIGH_SPEED_DL_MESSAGE}
              onRefreshVehicles={
                selectedHubId != null
                  ? () => fetchVehiclesForHub(selectedHubId)
                  : undefined
              }
            />
          )}

          {activeStep === 3 && (
            <SelectPlanStep
              plans={planOptions}
              selectedPlanId={selectedPlanId}
              onSelectPlan={handleEditBookingSelectPlan}
              onlyVehicleChangeNoPlan={onlyVehicleChangeNoPlan}
              onChooseNoPlanChange={handleNoPlanChangeClick}
              noPlanChangePlanLabel={bookingVehiclePlanSummary?.planName ?? ""}
              couponCode={couponCode}
              onCouponChange={setCouponCode}
              onApplyCoupon={handleApplyCoupon}
              additionalFees={additionalFees}
              onAdditionalFeesChange={setAdditionalFees}
              paymentMethod={paymentMethod}
              onPaymentMethodChange={setPaymentMethod}
              costBreakdown={costBreakdownForSelectPlan}
              totalPayable={displayTotalPayable}
              loading={plansLoading}
              currentBookingContext={bookingVehiclePlanSummary}
              selectedHubName={selectedHubDisplayName}
              selectedModelName={selectedVehicleModelDisplayName}
              yourSelectionVehicleDetails={yourSelectionVehicleDetails}
              costBreakdownLoading={costBreakdownLoading}
              newPlanWindowDisplay={
                onlyVehicleChangeNoPlan
                  ? noPlanChangeWindowDisplay
                  : newPlanWindowForEdit
                    ? {
                        start: newPlanWindowForEdit.planStartDisplay,
                        end: newPlanWindowForEdit.planEndDisplay,
                      }
                    : null
              }
              cashHandlingFees={cashPaymentCharge}
              onCashHandlingFeesChange={(value) => {
                const n = Number.parseFloat(String(value).trim());
                const v = Number.isFinite(n) && n >= 0 ? n : 0;
                setCashPaymentCharge(String(v));
                setCostBreakdown((prev) =>
                  prev.map((row) =>
                    row.label === "Cash Handling Fees"
                      ? {
                          ...row,
                          value: `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                        }
                      : row,
                  ),
                );
              }}
              lateFees={lateFeesAmount}
              onLateFeesChange={(value) => {
                const n = Number.parseFloat(String(value).trim());
                const v = Number.isFinite(n) && n >= 0 ? n : 0;
                setLateFeesAmount(String(v));
                setCostBreakdown((prev) =>
                  prev.map((row) =>
                    row.label === "Late fees"
                      ? {
                          ...row,
                          value: `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                        }
                      : row,
                  ),
                );
              }}
              promoCodeOptions={promoCodeOptions}
              promoCodesLoading={promoCodesLoading}
              couponApplyLoading={couponApplyLoading}
            />
          )}
        </Paper>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "flex-end",
            alignItems: "stretch",
            gap: 2,
            mt: { xs: 3, sm: 4 },
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            fullWidth={false}
            sx={{
              borderColor: THEME_PRIMARY,
              color: THEME_PRIMARY,
              minHeight: 42,
              px: 4,
              py: 1,
              borderRadius: "4px",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                borderColor: THEME_PRIMARY_LIGHT,
                bgcolor: THEME_PRIMARY_TINT,
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disabled={
              (activeStep === 0 && selectedHubId === null) ||
              (activeStep === 1 &&
                (selectedModelId == null ||
                  selectedModelId === "" ||
                  kycModelSelectionInvalid)) ||
              (activeStep === 2 &&
                (selectedVehicleId == null || selectedVehicleId === "")) ||
              (activeStep === 3 &&
                (costBreakdownLoading ||
                  (!onlyVehicleChangeNoPlan &&
                    (selectedPlanId == null || selectedPlanId === "")) ||
                  (onlyVehicleChangeNoPlan &&
                    !costBreakdownLoading &&
                    costBreakdown.length === 0))) ||
              saveLoading ||
              vehicleUnderbookingLoading
            }
            onClick={
              activeStep === steps.length - 1
                ? handleSaveClick
                : () => void handleNext()
            }
            fullWidth={false}
            sx={{
              bgcolor: "#FFC107",
              color: "#000",
              minHeight: 42,
              px: 4,
              py: 1,
              borderRadius: "4px",
              width: { xs: "100%", sm: "auto" },
              "&:hover": { bgcolor: "#FFB300" },
              "&.Mui-disabled": {
                bgcolor: "action.disabledBackground",
                color: "action.disabled",
              },
            }}
          >
            {saveLoading
              ? "Saving..."
              : vehicleUnderbookingLoading
                ? "Reserving vehicle..."
                : activeStep === steps.length - 1
                  ? "Save"
                  : "Next"}
          </Button>
        </Box>

        <Dialog
          open={saveConfirmOpen}
          onClose={() => {
            if (saveLoading) return;
            setSaveConfirmOpen(false);
          }}
          maxWidth="xs"
          fullWidth
          aria-labelledby="edit-booking-save-confirm-title"
          aria-describedby="edit-booking-save-confirm-desc"
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(22, 67, 135, 0.12)",
              boxShadow:
                "0 20px 50px rgba(22, 67, 135, 0.18), 0 4px 16px rgba(0, 0, 0, 0.06)",
            },
          }}
          BackdropProps={{
            sx: { backgroundColor: "rgba(22, 67, 135, 0.35)" },
          }}
        >
          <Box
            sx={{
              height: 4,
              width: "100%",
              background: `linear-gradient(90deg, ${THEME_PRIMARY} 0%, ${THEME_PRIMARY_LIGHT} 100%)`,
            }}
          />
          <Box sx={{ px: 3, pt: 3, pb: 0, textAlign: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                borderRadius: "50%",
                bgcolor: THEME_PRIMARY_TINT,
                color: THEME_PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid rgba(22, 67, 135, 0.15)`,
              }}
            >
              <SaveRoundedIcon sx={{ fontSize: 32 }} aria-hidden />
            </Box>
            <DialogTitle
              id="edit-booking-save-confirm-title"
              sx={{
                p: 0,
                mb: 1,
                fontSize: "1.25rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: THEME_PRIMARY,
                lineHeight: 1.3,
              }}
            >
              Save booking changes?
            </DialogTitle>
          </Box>
          <DialogContent sx={{ px: 3, pt: 0, pb: 1, textAlign: "center" }}>
            <Typography
              id="edit-booking-save-confirm-desc"
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.65,
                maxWidth: 320,
                mx: "auto",
              }}
            >
              Your updates will be sent to the server. If a payment is due,
              you&apos;ll complete it on the next step after confirming.
            </Typography>
            {paymentMethod === "online" &&
            !originalPlanPaymentMethodWasCash &&
            saveConfirmPayableNumeric > 0 ? (
              <FormControl sx={{ mt: 2, width: "100%", textAlign: "center" }}>
                <FormLabel
                  sx={{
                    color: THEME_PRIMARY,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    mb: 1,
                    "&.Mui-focused": { color: THEME_PRIMARY },
                  }}
                >
                  Payment request type
                </FormLabel>
                <RadioGroup
                  row
                  value={postSavePaymentMode}
                  onChange={(e) =>
                    setPostSavePaymentMode(
                      e.target.value as "payment_link" | "qr_code",
                    )
                  }
                  sx={{ justifyContent: "center", gap: 2 }}
                >
                  <FormControlLabel
                    value="payment_link"
                    control={<Radio size="small" />}
                    label="Payment link"
                  />
                  <FormControlLabel
                    value="qr_code"
                    control={<Radio size="small" />}
                    label="QR code"
                  />
                </RadioGroup>
              </FormControl>
            ) : null}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 1,
              gap: 1.25,
              flexDirection: { xs: "column-reverse", sm: "row" },
              justifyContent: "stretch",
              "& .MuiButton-root": {
                minHeight: 44,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9375rem",
              },
            }}
          >
            <Button
              onClick={() => setSaveConfirmOpen(false)}
              disabled={saveLoading}
              variant="outlined"
              fullWidth
              sx={{
                borderColor: THEME_PRIMARY,
                color: THEME_PRIMARY,
                "&:hover": {
                  borderColor: THEME_PRIMARY_LIGHT,
                  bgcolor: THEME_PRIMARY_TINT,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={saveLoading}
              variant="contained"
              fullWidth
              disableElevation
              sx={{
                bgcolor: THEME_PRIMARY,
                color: "#fff",
                boxShadow: "0 4px 14px rgba(22, 67, 135, 0.35)",
                "&:hover": {
                  bgcolor: THEME_PRIMARY_LIGHT,
                  boxShadow: "0 6px 18px rgba(22, 67, 135, 0.4)",
                },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "action.disabled",
                },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={onlineCollectionDialogOpen}
          onClose={() => {
            if (onlineCollectionLoading || verifyPaymentLoading) return;
            const done = onlineCollectionUpdateDone;
            setOnlineCollectionDialogOpen(false);
            setOnlineCollectionError(null);
            setOnlineCollectionPaymentLink("");
            setOnlineCollectionQrSrc("");
            setOnlineCollectionOrderId("");
            setOnlineCollectionVerifyEntityId(null);
            setOnlineCollectionUpdateDone(false);
            if (done) {
              navigate("/booking-management", {
                state: { shouldRefetch: true },
              });
            }
          }}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={onlineCollectionLoading || verifyPaymentLoading}
          aria-labelledby="edit-booking-online-collection-title"
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(22, 67, 135, 0.12)",
              boxShadow:
                "0 20px 50px rgba(22, 67, 135, 0.18), 0 4px 16px rgba(0, 0, 0, 0.06)",
            },
          }}
        >
          <DialogTitle
            id="edit-booking-online-collection-title"
            sx={{
              fontSize: "1.15rem",
              fontWeight: 700,
              color: THEME_PRIMARY,
              pb: 1,
            }}
          >
            {postSavePaymentMode === "payment_link"
              ? "Payment link"
              : "Payment QR code"}
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center", pt: 0 }}>
            {onlineCollectionLoading ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress sx={{ color: THEME_PRIMARY }} />
              </Box>
            ) : null}
            {onlineCollectionError ? (
              <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                {onlineCollectionError}
              </Alert>
            ) : null}
            {!onlineCollectionLoading &&
            postSavePaymentMode === "payment_link" &&
            onlineCollectionPaymentLink ? (
              <Box sx={{ mt: 1, textAlign: "left", width: "100%" }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ width: "100%" }}
                >
                  <Link
                    href={onlineCollectionPaymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      flex: 1,
                      wordBreak: "break-all",
                      fontSize: "0.95rem",
                      textAlign: "left",
                    }}
                  >
                    {onlineCollectionPaymentLink}
                  </Link>
                  <IconButton
                    aria-label="Copy payment link"
                    size="small"
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(onlineCollectionPaymentLink)
                        .then(() =>
                          showNotification("Payment link copied.", "success"),
                        )
                        .catch(() =>
                          showNotification("Could not copy link.", "error"),
                        );
                    }}
                    sx={{ flexShrink: 0, mt: -0.25 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ) : null}
            {!onlineCollectionLoading &&
            postSavePaymentMode === "qr_code" &&
            onlineCollectionQrSrc ? (
              <Box sx={{ mt: 1, width: "100%", textAlign: "center" }}>
                <Box
                  component="img"
                  src={onlineCollectionQrSrc}
                  alt="Payment QR code"
                  sx={{
                    maxWidth: 280,
                    width: "100%",
                    height: "auto",
                    mx: "auto",
                    display: "block",
                    borderRadius: 1,
                    border: "1px solid rgba(22, 67, 135, 0.12)",
                  }}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ width: "100%", mt: 1.5 }}
                >
                  <Link
                    href={onlineCollectionQrSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      flex: 1,
                      wordBreak: "break-all",
                      fontSize: "0.9rem",
                      textAlign: "left",
                    }}
                  >
                    {onlineCollectionQrSrc}
                  </Link>
                  <IconButton
                    aria-label="Copy QR link"
                    size="small"
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(onlineCollectionQrSrc)
                        .then(() =>
                          showNotification("QR link copied.", "success"),
                        )
                        .catch(() =>
                          showNotification("Could not copy link.", "error"),
                        );
                    }}
                    sx={{ flexShrink: 0, mt: -0.25 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              pt: 1,
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {postSavePaymentMode === "qr_code" &&
            onlineCollectionQrSrc &&
            !onlineCollectionLoading ? (
              <Button
                variant="contained"
                disabled={verifyPaymentLoading}
                onClick={() => void handleVerifyQrPayment()}
                sx={{
                  bgcolor: THEME_PRIMARY,
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 120,
                  "&:hover": { bgcolor: THEME_PRIMARY_LIGHT },
                }}
              >
                {verifyPaymentLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Verify"
                )}
              </Button>
            ) : null}
            <Button
              variant="outlined"
              disabled={onlineCollectionLoading || verifyPaymentLoading}
              onClick={() => {
                if (onlineCollectionLoading || verifyPaymentLoading) return;
                const done = onlineCollectionUpdateDone;
                setOnlineCollectionDialogOpen(false);
                setOnlineCollectionError(null);
                setOnlineCollectionPaymentLink("");
                setOnlineCollectionQrSrc("");
                setOnlineCollectionOrderId("");
                setOnlineCollectionVerifyEntityId(null);
                setOnlineCollectionUpdateDone(false);
                if (done) {
                  navigate("/booking-management", {
                    state: { shouldRefetch: true },
                  });
                }
              }}
              sx={{
                borderColor: THEME_PRIMARY,
                color: THEME_PRIMARY,
                textTransform: "none",
                fontWeight: 600,
                minWidth: 120,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default EditBookingPage;
